import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle, Ban, CheckCircle2, Download, FileCog, Map as MapIcon, Package, Pencil, Send, Sparkles, Undo2, Upload, Wallet,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useAuthStore } from "@/store/auth";
import { extractApplicationFeeDoc, type PoaFeeCandidate } from "../../api";
import { RequisitionApprovalNotice } from "@/features/orders/components/RequisitionApprovalNotice";
import { EdeliveryLine, EmptyState, FileDrop, PageHeader, PaperDispatchLine, SectionCard } from "../../components/ui";
import { POA_FEE_STATUS_LABELS as FEE_LABELS, PoaFeeOptionGroups, UnpaidPoaFeeNote } from "../../components/PoaFeeOptions";
import { PoaSuggestionHint } from "../../components/PoaSuggestionHint";
import { StampDutyOfficePicker } from "../../components/StampDutyOfficePicker";
import { InfoHint } from "../../components/wnioski/InfoHint";
import { EdeliveryRecipientHint } from "../../components/wnioski/EdeliveryRecipientHint";
import { CompleteApplicationModal } from "../../components/wnioski/CompleteApplicationModal";
import { DecisionOriginalCard } from "../../components/wnioski/DecisionOriginalCard";
import { CaseLinkPicker, type CaseLink } from "../../components/wnioski/CaseLinkPicker";
import { DetachAutoCaseModal } from "../../components/DetachAutoCaseModal";
import { WithdrawSubmissionModal } from "../../components/wnioski/WithdrawSubmissionModal";
import { KwWarningConfirmModal } from "../WpwMassPackagePage";
import { EditApplicationModal } from "../../components/wnioski/EditApplicationModal";
import { GenerateApplicationDocModal } from "../../components/wnioski/GenerateApplicationDocModal";
import { OdtEditorScreen } from "@/features/doc_editor/components/OdtEditorScreen";
import { useEditorAvailability } from "@/features/doc_editor/hooks";
import type { EditorDocKind } from "@/features/doc_editor/types";
import { GenerateFormatkiModal } from "../../components/wnioski/GenerateFormatkiModal";
import { MergeWypisModal } from "../../components/wnioski/MergeWypisModal";
import { RoadDataCard } from "../../components/wnioski/RoadDataCard";
import { RegulacjaAreaCard } from "../../components/wnioski/RegulacjaAreaCard";
import { UlicpAreaCard } from "../../components/wnioski/UlicpAreaCard";
import {
  useApplication,
  useApplicationAutoMaps,
  useApplicationMutations,
  useApplicationPaidFeeCandidates,
  useApplicationPoaCandidates,
  useGeneratedHistory,
  useApplicationFeeCandidates,
  usePoaFeeCandidates,
  usePoaFeeSuggestion,
  useSelectableOffices,
  useApplicationsOverview,
  useTaskPolling,
} from "../../hooks";
import {
  APPLICATION_STATUS_COLORS,
  APPLICATION_STATUS_LABELS,
  type ApplicationPhaseKey,
  type ApplicationFeeInfo,
  type OfficialApplicationDetail,
  type QualificationRow,
  ITEM_OUTCOME_OPTIONS,
  SEPARATE_FILE_ROLE_LABELS,
} from "../../types";
import { downloadApplicationPackageZip, downloadWnioskWypisPages,
  renderSheetSetPdf as apiRenderSheetSetPdf,
  uploadSheetSetPdfs as apiUploadSheetSetPdfs } from "../../api";
import { fmtDate, fmtDateTime } from "../../utils";
import { APPLICATION_APPROVAL_ENABLED } from "../../applicationApprovalFeature";

function apiError(e: unknown): string {
  const detail = (e as { response?: { data?: { errors?: { detail?: unknown } } } })
    ?.response?.data?.errors?.detail;
  return typeof detail === "string" ? detail : "Operacja nie powiodła się.";
}

function apiErrorCode(e: unknown): string {
  const code = (e as { response?: { data?: { errors?: { code?: unknown } } } })
    ?.response?.data?.errors?.code;
  return typeof code === "string" ? code : "";
}

/** Separator „LUB” między alternatywnymi drogami do dokumentu wniosku — komplet
    formularzy z mapy, generat z szablonu i własny plik wykluczają się w pakiecie. */
function OrDivider() {
  return (
    <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-text-muted)]">
      <span className="h-px flex-1 bg-[var(--color-border)]" />
      LUB
      <span className="h-px flex-1 bg-[var(--color-border)]" />
    </div>
  );
}

/** Karta fazy: nagłówek ze stanem, „i” z instrukcją, akcje zatwierdź/cofnij. */
function PhaseCard({
  app, phase, hint, children, onApprove, onReopen, error, errorAction,
}: {
  app: OfficialApplicationDetail;
  phase: ApplicationPhaseKey;
  hint: string;
  children: React.ReactNode;
  onApprove: () => void;
  onReopen: () => void;
  error?: string;
  /** Wyjście z błędu wprost pod komunikatem (np. świadome zatwierdzenie mimo przeszkody). */
  errorAction?: React.ReactNode;
}) {
  const meta = app.phases.find((p) => p.key === phase);
  if (!meta) return null;
  const phases = app.phases.map((p) => p.key);
  const index = phases.indexOf(phase);
  const unlocked = app.phases.slice(0, index).every((p) => p.state === "approved");
  const approved = meta.state === "approved";
  // pending_approval: wniosek zamrożony w kolejce dyrektora — fazy bez przycisków
  // (backend i tak odmawia); edycję odblokowuje „Wycofaj z akceptacji". Anulowany (także
  // wycofany po złożeniu) jest zamknięty — bez przycisków, a „Wysyłka" wyszarzona.
  const cancelled = app.status === "cancelled";
  const submitted = app.status === "submitted" || app.status === "completed"
    || app.status === "pending_approval" || cancelled;
  return (
    <SectionCard
      className={clsx(((!unlocked && !approved) || (cancelled && phase === "submit")) && "opacity-60")}
      title={
        <span className="flex items-center gap-2">
          <span className={clsx(
            "inline-flex h-5 w-5 items-center justify-center text-[10px] font-mono border",
            approved ? "bg-green-100 border-green-400 text-green-800"
                     : "border-[var(--color-border-strong)]",
          )}>
            {approved ? "✓" : index + 1}
          </span>
          {meta.label}
          <InfoHint text={hint} />
          {approved && meta.approved_by && (
            <span className="text-[10px] text-[var(--color-text-muted)] normal-case tracking-normal">
              zatwierdził(a) {meta.approved_by}
            </span>
          )}
          {approved && meta.fees_unpaid && (
            <span className="normal-case tracking-normal">
              <Badge color="yellow">bez potwierdzenia opłaty</Badge>
            </span>
          )}
        </span>
      }
      action={
        submitted ? undefined : approved ? (
          <Button size="sm" variant="secondary" onClick={onReopen}>
            <Undo2 size={12} className="mr-1" /> Cofnij do edycji
          </Button>
        ) : unlocked ? (
          <Button size="sm" onClick={onApprove}>Zatwierdź fazę</Button>
        ) : undefined
      }
    >
      {error && (
        <div className="text-xs text-red-600 mb-2">
          {error}
          {errorAction && <div className="mt-1.5">{errorAction}</div>}
        </div>
      )}
      {children}
    </SectionCard>
  );
}

// Opłata „żywa", ale jeszcze bez potwierdzenia przelewu — a to potwierdzenie (PDF)
// jedzie do urzędu razem z pełnomocnictwem (w pakiecie albo, gdy samo ma podpis
// elektroniczny, osobnym plikiem obok), więc jego brak blokuje kompilację i wysyłkę.
const UNPAID_FEE_STATUSES = ["pending_approval", "approved"];

// Board milestone the procedure advances (registry milestone_key → display name).
const MILESTONE_LABELS: Record<string, string> = {
  wnioski: "WNIOSKI",
  mpzp_ulicp: "MPZP/ULICP",
  odrolnienie: "ODROLNIENIE",
};

/** ULICP — parametry obiektu z Załącznika C druku (C.4.1/C.4.4/C.4.5/C.4.6).
 *
 *  AI układa komplet jednym wywołaniem z wykazu urządzeń mapy: powierzchnia to grunt
 *  TRWALE zajęty przez fundamenty złączy/stacji/słupów (kabel w ziemi nie zajmuje go
 *  trwale), a szerokość i wysokość dotyczą najbardziej gabarytowego obiektu naziemnego.
 *  Każde pole zostaje edytowalne — projektant zna urządzenie lepiej niż model. */
function UlicpObjectParams({
  app, editable,
}: {
  app: OfficialApplicationDetail;
  editable: boolean;
}) {
  const m = useApplicationMutations();
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [err, setErr] = useState("");
  const values = draft ?? {
    powierzchnia: app.ulicp_object_params?.powierzchnia ?? "",
    szerokosc: app.ulicp_object_params?.szerokosc ?? "",
    wysokosc: app.ulicp_object_params?.wysokosc ?? "",
    uwagi: app.ulicp_object_params?.uwagi ?? "",
  };
  const commit = () => {
    if (!draft) return;
    m.update.mutate(
      { id: app.id, payload: { ulicp_object_params: draft } },
      { onSuccess: () => setDraft(null), onError: (e) => setErr(apiError(e)) },
    );
  };
  const field = (key: string, label: string, hint: string) => (
    <label key={key} className="block">
      <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
        {label}
      </span>
      <input
        className="input-base w-full py-0.5 text-xs"
        value={values[key] ?? ""}
        placeholder={hint}
        disabled={!editable}
        onChange={(e) => setDraft({ ...values, [key]: e.target.value })}
        onBlur={commit}
      />
    </label>
  );
  return (
    <div className="space-y-1 border-t border-[var(--color-border)] pt-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">
          Parametry obiektu — Załącznik C druku{" "}
          <span className="font-normal text-[var(--color-text-muted)]">
            (AI bierze dane z mapy; poprawisz każde pole)
          </span>
        </span>
        <Button
          size="sm" variant="secondary"
          loading={m.generateUlicpParams.isPending}
          disabled={!editable}
          onClick={() => {
            setErr("");
            m.generateUlicpParams.mutate(app.id, {
              onSuccess: () => setDraft(null),
              onError: (e) => setErr(apiError(e)),
            });
          }}
        >
          <Sparkles size={13} className="mr-1" /> Ułóż parametry AI
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {field("powierzchnia", "C.4.1 Powierzchnia [m²]", "grunt trwale zajęty")}
        {field("szerokosc", "C.4.4 Szerokość [m]", "np. 0,6 (złącze)")}
        {field("wysokosc", "C.4.5 Wysokość [m]", "np. 1,7 (złącze)")}
      </div>
      <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
          C.4.6 Inne parametry
        </span>
        <textarea
          className="input-base !h-auto w-full !py-1"
          rows={2}
          placeholder="np. Linie kablowe oraz przyłącza kablowe układać na głębokości min. 0,8 m. Szerokość i wysokość dotyczą wolnostojącego złącza kablowo-pomiarowego wraz z fundamentem."
          value={values.uwagi ?? ""}
          disabled={!editable}
          onChange={(e) => setDraft({ ...values, uwagi: e.target.value })}
          onBlur={commit}
        />
      </label>
      <div className="text-[var(--color-text-muted)]">
        Powierzchnia z C.4.1 to grunt TRWALE zajęty przez fundamenty (złącza, stacje,
        słupy) — nie długość kabla. Wymiary dotyczą największego obiektu naziemnego;
        gdy na trasie nie ma żadnego, zostaw kreski.
      </div>
      {err && <div className="text-amber-700">⚠ {err}</div>}
    </div>
  );
}

function QualificationOverrides({
  app, row, editable, mutate,
}: {
  app: OfficialApplicationDetail;
  row: QualificationRow;
  editable: boolean;
  mutate: {
    mutate: (v: {
      id: string;
      overrides: {
        parcel_id: string;
        override?: boolean | null;
        exclusion_area_override_m2?: number | null;
        exclusion_area_note?: string;
        exclusion_land_class?: string;
      }[];
    }) => void;
  };
}) {
  const auto = row.exclusion_area_m2 ?? 0;
  const [area, setArea] = useState(
    row.exclusion_area_override_m2 != null ? String(row.exclusion_area_override_m2) : "",
  );
  const [klasa, setKlasa] = useState(row.exclusion_land_class ?? "");

  const save = (payload: {
    exclusion_area_override_m2?: number | null;
    exclusion_land_class?: string;
  }) => mutate.mutate({ id: app.id, overrides: [{ parcel_id: row.parcel_id, ...payload }] });

  return (
    <span className="flex items-center gap-1 ml-auto shrink-0">
      <input
        className="w-20 px-1 py-0.5 text-[11px] font-mono border border-[var(--color-border)] bg-transparent"
        value={area}
        disabled={!editable}
        placeholder={auto ? `${auto} m²` : "m²"}
        title={`Powierzchnia do wyłączenia. Automat: ${auto} m² (stawki × urządzenia na działce). Wpisz własną, żeby nadpisać; puste = wróć do wyliczonej.`}
        onChange={(e) => setArea(e.target.value)}
        onBlur={() => {
          const current = row.exclusion_area_override_m2 != null ? String(row.exclusion_area_override_m2) : "";
          if (area !== current) {
            const parsed = Number(area.trim().replace(",", "."));
            save({
              exclusion_area_override_m2:
                area.trim() === "" || Number.isNaN(parsed) ? null : parsed,
            });
          }
        }}
      />
      <input
        className="w-24 px-1 py-0.5 text-[11px] font-mono border border-[var(--color-border)] bg-transparent"
        value={klasa}
        disabled={!editable}
        placeholder="klasa"
        title="Klasa gruntu wpisywana do wniosku. Liczona z odczytu klasoużytku pod urządzeniem, potem wypisu, potem geoportalu — wpisz własną tylko, gdy wiesz lepiej."
        onChange={(e) => setKlasa(e.target.value)}
        onBlur={() => {
          if ((klasa ?? "") !== (row.exclusion_land_class ?? "")) {
            save({ exclusion_land_class: klasa.trim() });
          }
        }}
      />
      {row.exclusion_area_override_m2 != null && (
        <span title="Powierzchnia nadpisana ręcznie — automat liczy inaczej">⚠</span>
      )}
    </span>
  );
}

export function ApplicationPackagePage() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const qc = useQueryClient();
  const { data: app, isLoading } = useApplication(applicationId);
  const m = useApplicationMutations(applicationId);
  const { data: users } = useInternalUsers();

  // Preselekcja zakresu (obręb po obrębie, z powodem przy każdej działce) — pokazywana
  // tylko do odczytu: wniosek ma już swój zakres, a tu widać, co automat wziął i czego
  // NIE wziął, żeby nie trzeba było zgadywać, czemu działki nie ma w pakiecie.
  const { data: scopeOverview } = useApplicationsOverview(
    app?.application_type === "odrolnienie" ? app?.connection : undefined,
  );
  const [signedDocPrompt, setSignedDocPrompt] = useState(false);
  const [signedPackagePrompt, setSignedPackagePrompt] = useState(false);
  // Edycja wniosku (zakres/adresat/uwagi) przed zatwierdzeniem fazy zakresu.
  const [editOpen, setEditOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  // Podpięcie pakietu pod istniejącą sprawę z Korespondencji (przed wysyłką).
  const [caseLinkOpen, setCaseLinkOpen] = useState(false);
  const [caseDraft, setCaseDraft] = useState<CaseLink | null>(null);
  // Odpięcie złożonego pakietu od sprawy założonej przy jego wysyłce — usuwa sprawę.
  const [detachAutoOpen, setDetachAutoOpen] = useState(false);
  // Wycofanie złożonego wniosku, zanim pismo wyszło (nieodwracalne).
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [phaseErrors, setPhaseErrors] = useState<Record<string, string>>({});
  const [scopeWarnings, setScopeWarnings] = useState<string[]>([]);
  const [poaWarnings, setPoaWarnings] = useState<string[]>([]);
  const [poaSummary, setPoaSummary] = useState("");
  // Ręczne podpinanie PoA z rejestru (panel kandydatów pod auto-przyciskiem).
  const [poaPickerOpen, setPoaPickerOpen] = useState(false);
  // Modal opłaty otwiera się albo „normalnie" (sugestia decyduje o trybie), albo wprost
  // w trybie „niepotrzebna" — z przycisku przy ogniwie łańcucha.
  const [feeModalLink, setFeeModalLink] =
    useState<{ linkId: string; mode?: "not_required" } | null>(null);
  // Modal opłaty skarbowej OD WNIOSKU (ULICP 598 zł) — druga opłata wniosku, obok 17 zł
  // za pełnomocnictwo. `mode` otwiera go wprost w trybie „niepotrzebna".
  const [appFeeModal, setAppFeeModal] = useState<{ mode?: "not_required" } | null>(null);
  const [settleFeesConfirm, setSettleFeesConfirm] = useState(false);
  // Bramka fazy „poa” odbiła WYŁĄCZNIE na opłacie bez potwierdzenia przelewu (kod
  // `unpaid_fee`) — pod błędem pojawia się wtedy „Zatwierdź bez potwierdzenia opłaty”.
  const [poaUnpaidFeeOnly, setPoaUnpaidFeeOnly] = useState(false);
  const [unpaidFeeApproveConfirm, setUnpaidFeeApproveConfirm] = useState(false);
  // Kompilacja przed przelewem — tylko po potwierdzeniu w oknie (kompilat bez opłaty).
  const [compileWithoutFeeConfirm, setCompileWithoutFeeConfirm] = useState(false);
  // Wysyłka przed przelewem — tylko po potwierdzeniu w oknie (wniosek bez opłaty).
  const [submitWithoutFeeConfirm, setSubmitWithoutFeeConfirm] = useState(false);
  const [docModal, setDocModal] = useState(false);
  // Edytor pisma w przeglądarce (Collabora na pełnym ekranie): slot = (rodzaj, id dokumentu).
  const [editorDoc, setEditorDoc] = useState<{ kind: EditorDocKind; id: string; label: string } | null>(null);
  const { data: editorAvailability } = useEditorAvailability();
  const editorAvailable = !!editorAvailability?.available;
  const [zipBusy, setZipBusy] = useState(false);
  // Historia generatów pisma (wszystkie wersje ODT, najnowsza pierwsza).
  const [docHistoryOpen, setDocHistoryOpen] = useState(false);
  const { data: docHistory } = useGeneratedHistory("official_application", applicationId ?? "");
  // Draft opisu ENERGA (null = pokazuj wartość z API); zapis PATCH-em na blur.
  const [energaDraft, setEnergaDraft] = useState<string | null>(null);
  const [energaErr, setEnergaErr] = useState("");
  // Dane drogi krajowej GDDKiA (kilometraż z API mapy GDDKiA / ręczna korekta).
  const [roadErr, setRoadErr] = useState("");
  // Generowanie formatek dla działek pakietu (modal → task w tle → ZIP w „Znalezionych
  // planach"; dołączenie do pakietu następuje po wgraniu splotowanych PDF-ów).
  const [formatkiModal, setFormatkiModal] = useState(false);
  const [formatkiTask, setFormatkiTask] = useState<{ taskId: string; sheetSetId: string } | null>(null);
  const [formatkiErr, setFormatkiErr] = useState("");
  const [formatkiDone, setFormatkiDone] = useState(false);
  // Komplet formularzy zgody WPW (4a+4b+4c naraz → jeden scalony PDF) — task w tle,
  // bo każdy druk to osobna konwersja LibreOffice.
  const [wpwFormsTask, setWpwFormsTask] = useState<string | null>(null);
  // Ostrzeżenie KW (właściciele niezgodni z księgą) NIE blokuje, ale generacja
  // formularzy i kompilacja żądają jawnego potwierdzenia — jak w kreatorze masowym.
  const [kwPrompt, setKwPrompt] = useState<{ actionLabel: string; proceed: () => void } | null>(null);
  const [wpwFormsErr, setWpwFormsErr] = useState("");
  // Szkice opisów per rodzaj formularza WPW (klucz = kind); brak klucza = bez edycji.
  const [wpwDescDrafts, setWpwDescDrafts] = useState<Record<string, string>>({});
  // Sklejanie stron wypisów działek w jeden załącznik „wypis" (modal wyboru stron).
  const [mergeWypisOpen, setMergeWypisOpen] = useState(false);
  // Picker opłaconych opłat (/official-fees) w fazie załączników: kind pozycji
  // checklisty, dla której jest otwarty (null = zamknięty).
  const [feePickerKind, setFeePickerKind] = useState<string | null>(null);
  const formatkiPoll = useTaskPolling(formatkiTask?.taskId ?? null);
  const formatkiStatus = formatkiPoll.data?.status;
  const formatkiRunning = !!formatkiTask
    && !["success", "failure", "cancelled"].includes(formatkiStatus ?? "");
  useEffect(() => {
    if (!formatkiTask || !formatkiStatus) return;
    if (formatkiStatus === "success") {
      // ZIP gotowy — pokaże się w „Znalezionych planach" (pobierz + wgraj PDF-y)
      qc.invalidateQueries({ queryKey: ["pp", "app-auto-maps", applicationId] });
      setFormatkiDone(true);
      setFormatkiTask(null);
    } else if (["failure", "cancelled"].includes(formatkiStatus)) {
      setFormatkiErr("Generowanie formatek nie powiodło się — sprawdź kreator formatek.");
      setFormatkiTask(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatkiStatus]);

  const wpwFormsPoll = useTaskPolling(wpwFormsTask);
  const wpwFormsStatus = wpwFormsPoll.data?.status;
  const wpwFormsRunning = !!wpwFormsTask
    && !["success", "failure", "cancelled"].includes(wpwFormsStatus ?? "");
  useEffect(() => {
    if (!wpwFormsTask || !wpwFormsStatus) return;
    if (wpwFormsStatus === "success") {
      // Komplet wszedł jako dokument wniosku — odśwież wniosek i historię generatów.
      qc.invalidateQueries({ queryKey: ["pp", "application", applicationId] });
      qc.invalidateQueries({ queryKey: ["doc-templates", "generated-history"] });
      setWpwFormsTask(null);
    } else if (["failure", "cancelled"].includes(wpwFormsStatus)) {
      // Powód (brak szablonu, bramka KW, numer pełnomocnictwa) siedzi w logu zadania.
      setWpwFormsErr(
        wpwFormsPoll.data?.log?.trim().split("\n").slice(-1)[0]
        || "Generowanie formularzy nie powiodło się.",
      );
      setWpwFormsTask(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wpwFormsStatus]);

  // Upload splotowanych PDF-ów arkuszy wprost z fazy MAPY (zestaw „bez PDF-a"):
  // backend scala je w PLAN_SYTUACYJNY.pdf, po scaleniu dołączamy do pakietu.
  const [sheetPdfBusy, setSheetPdfBusy] = useState<string | null>(null);
  // Scalenie NA SERWERZE — druga droga do tego samego PDF-a, bez plotowania w CAD-zie.
  // Do 01.09.2026 jedyną drogą był ręczny plot: akcja `render-pdf` istniała, ale nie
  // miała w aplikacji żadnego wywołania, więc pakiet WPW bez projektanta stał.
  const renderSheetPdf = async (sheetSetId: string) => {
    setFormatkiErr("");
    setSheetPdfBusy(sheetSetId);
    try {
      await apiRenderSheetSetPdf(sheetSetId);
      m.attachMap.mutate(
        { id: applicationId!, sheetSetId },
        { onError: (e) => setFormatkiErr(apiError(e)) },
      );
      qc.invalidateQueries({ queryKey: ["pp", "app-auto-maps", applicationId] });
    } catch (e) {
      setFormatkiErr(apiError(e));
    } finally {
      setSheetPdfBusy(null);
    }
  };

  const uploadSheetPdfs = async (sheetSetId: string, files: File[]) => {
    setFormatkiErr("");
    setSheetPdfBusy(sheetSetId);
    try {
      await apiUploadSheetSetPdfs(sheetSetId, files);
      m.attachMap.mutate(
        { id: applicationId!, sheetSetId },
        { onError: (e) => setFormatkiErr(apiError(e)) },
      );
      qc.invalidateQueries({ queryKey: ["pp", "app-auto-maps", applicationId] });
    } catch (e) {
      setFormatkiErr(apiError(e));
    } finally {
      setSheetPdfBusy(null);
    }
  };

  const [compileTaskId, setCompileTaskId] = useState<string | null>(null);
  const compileTask = useTaskPolling(compileTaskId);
  const compiling = !!compileTaskId
    && !["success", "failure", "cancelled"].includes(compileTask.data?.status ?? "");
  const [submitChannel, setSubmitChannel] = useState("paper");
  const [submitRef, setSubmitRef] = useState("");
  // Zwrot do poprawy (dyrektor w kolejce „do akceptacji"): modal potwierdzenia
  // z opcjonalnym powodem — trafia on do autora (baner na wniosku + dzwonek).
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  // Moduł e-Doręczeń (DLC_09) bywa wyłączony globalnie — wtedy w ogóle nie ma go na
  // liście dostępów i kanał e-Doręczeń nie pojawia się w formularzu wysyłki.
  const currentUser = useAuthStore((st) => st.user);
  const edeliveryEnabled = !!currentUser?.accessible_dlc_modules?.includes("dlc_09");

  const mapsPhaseActive = !!app?.phases.some((p) => p.key === "maps");
  const { data: autoMaps } = useApplicationAutoMaps(applicationId, mapsPhaseActive);
  const { data: feeCandidates } = usePoaFeeCandidates(!!feeModalLink);
  const { data: appFeeCandidates } = useApplicationFeeCandidates(applicationId ?? "", !!appFeeModal);
  const { data: paidFees } = useApplicationPaidFeeCandidates(applicationId ?? "", !!feePickerKind);
  const { data: poaCandidates } = useApplicationPoaCandidates(applicationId ?? "", poaPickerOpen);

  useEffect(() => {
    const s = compileTask.data?.status;
    if (compileTaskId && s && ["success", "failure", "cancelled"].includes(s)) {
      qc.invalidateQueries({ queryKey: ["pp", "application", applicationId] });
    }
  }, [compileTask.data?.status, compileTaskId, applicationId, qc]);

  const setErr = (phase: string, message: string) => {
    setPhaseErrors((s) => ({ ...s, [phase]: message }));
    if (phase === "poa") setPoaUnpaidFeeOnly(false);
  };
  const withKwWarningConfirm = (actionLabel: string, proceed: () => void) => {
    if (app?.wpw_kw_warning) setKwPrompt({ actionLabel, proceed });
    else proceed();
  };
  // Plansza ostrzeżenia KW: z danych wniosku, więc zostaje na ekranie tak długo, jak
  // rozbieżność istnieje — nie znika po zamknięciu zadania ani po odświeżeniu.
  const kwWarningNotice = app?.wpw_kw_warning ? (
    <div
      className="flex items-start gap-2 border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200 px-3 py-2"
      data-testid="kw-warning-notice"
    >
      <AlertTriangle size={14} className="mt-0.5 shrink-0" />
      <span>
        <b>Właściciele niezgodni z KW.</b> {app.wpw_kw_warning.reason} Formularze i pakiet
        powstaną dla właścicieli wpisanych na działce — sprawdź ich z Działem II księgi,
        zanim pakiet pójdzie w teren (zakładka Działki / KW).
      </span>
    </div>
  ) : null;
  const approve = (phase: ApplicationPhaseKey) => {
    setErr(phase, "");
    // Brak podpisanego druku to nie „błąd walidacji do przeczytania w czerwonej linijce",
    // tylko brakujący KROK — pokazujemy go osobnym oknem z instrukcją, co zrobić.
    if (phase === "document" && app!.application_type === "odrolnienie" && !app!.own_document) {
      setSignedDocPrompt(true);
      return;
    }
    // To samo dla podpisu na GOTOWYM pakiecie (ULICP): kompilat jest, podpisanego brak.
    if (phase === "compile" && app!.config?.signed_package && app!.compiled_pdf && !app!.compiled_is_manual) {
      setSignedPackagePrompt(true);
      return;
    }
    m.approvePhase.mutate({ id: app!.id, phase }, {
      onError: (e) => {
        setErr(phase, apiError(e));
        if (phase === "poa" && apiErrorCode(e) === "unpaid_fee") setPoaUnpaidFeeOnly(true);
      },
    });
  };
  const reopen = (phase: ApplicationPhaseKey) => {
    setErr(phase, "");
    m.reopenPhase.mutate({ id: app!.id, phase }, { onError: (e) => setErr(phase, apiError(e)) });
  };

  const userOptions = useMemo(
    () => (users ?? []).map((u) => ({ value: String(u.id), label: u.full_name || u.username })),
    [users],
  );

  // Gminy działek pakietu (fallback: gmina adresata) — na jednym terenie bywają
  // dwie różne gminy, więc okno generowania map mówi, dla jakiej gminy powstaje mapa.
  const packageCommunes = useMemo(() => {
    const names = [
      ...new Set(
        (app?.parcels_brief ?? []).map((p) => (p.commune ?? "").trim()).filter(Boolean),
      ),
    ].sort((a, b) => a.localeCompare(b, "pl"));
    if (names.length) return names;
    return app?.commune ? [app.commune] : [];
  }, [app]);

  if (isLoading || !app) return <div className="py-16 flex justify-center"><Spinner /></div>;
  const config = app.config;
  const editable = !["pending_approval", "submitted", "completed", "cancelled"].includes(app.status);
  const scopeApproved = app.phases.find((p) => p.key === "scope")?.state === "approved";
  // Opłata podpięta PRZED opłaceniem (nowa albo istniejąca): bez potwierdzenia przelewu
  // pakiet jest niekompletny — kompilacja i wysyłka tylko świadomie, po potwierdzeniu
  // w oknie (backend bez flagi `allow_unpaid_fees` odmawia).
  const unpaidFeeLinks = app.poa_fees_settled_externally
    ? []
    : app.poas.filter((l) => l.fee && UNPAID_FEE_STATUSES.includes(l.fee_status ?? ""));
  // To samo dla opłaty OD WNIOSKU (ULICP 598 zł).
  const appFee = app.application_fee_info?.fee;
  const unpaidAppFee = !app.poa_fees_settled_externally && appFee
    && UNPAID_FEE_STATUSES.includes(appFee.status) ? appFee : null;
  const unpaidFeeNames = [
    ...(unpaidAppFee ? [`od wniosku (${unpaidAppFee.amount} zł)`] : []),
    ...(unpaidFeeLinks.length
      ? [`za pełnomocnictwo (${unpaidFeeLinks.map((l) => l.poa.attorney_display).join(", ")})`]
      : []),
  ];
  const hasUnpaidFees = unpaidFeeNames.length > 0;
  const unpaidFeeSubject = (unpaidFeeNames.length > 1 ? "Opłaty skarbowe " : "Opłata skarbowa ")
    + unpaidFeeNames.join(" i ");
  // Kompilat sprzed przelewu (świadoma kompilacja bez opłaty albo przelew potwierdzony
  // dopiero po kompilacji) — backend porównuje datę kompilacji z datą potwierdzenia.
  const packageMissingFees = app.package_missing_fees ?? [];
  const packageMissingFeesPaid = packageMissingFees.length > 0 && packageMissingFees.every((f) => f.paid);
  const packageMissingFeesLabel = packageMissingFees.map((f) => f.label).join(" i ");
  // Wysłany bez opłaty: pliki obok pakietu dokłada wysyłka tylko w chwili złożenia,
  // więc potwierdzenie przelewu trzeba dosłać do urzędu ręcznie.
  const submittedMissingFees = app.submitted_missing_fees ?? [];
  const runSubmit = (allowUnpaidFees = false) => {
    setErr("submit", "");
    m.submit.mutate({ id: app.id, channel: submitChannel, channelRef: submitRef, allowUnpaidFees }, {
      onSuccess: () => setSubmitWithoutFeeConfirm(false),
      onError: (e) => {
        setSubmitWithoutFeeConfirm(false);
        setErr("submit", apiError(e));
      },
    });
  };
  const runCompile = (allowUnpaidFees = false) => {
    setErr("compile", "");
    m.compile.mutate({ id: app.id, allowUnpaidFees }, {
      onSuccess: (r) => {
        setCompileWithoutFeeConfirm(false);
        setCompileTaskId(r.task_id);
      },
      onError: (e) => {
        setCompileWithoutFeeConfirm(false);
        setErr("compile", apiError(e));
      },
    });
  };
  // „Wycofaj z akceptacji" (pending_approval) — only the requester or the author
  // (app.can_withdraw_approval); approvers send it back with „Zwróć do poprawy".
  const withdrawButton = (
    <Button variant="secondary" loading={m.withdrawApproval.isPending}
            onClick={() => {
              setErr("submit", "");
              m.withdrawApproval.mutate(app.id, {
                onError: (e) => setErr("submit", apiError(e)),
              });
            }}>
      <Undo2 size={13} className="mr-1" /> Wycofaj z akceptacji
    </Button>
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {app.number}
            <Badge color={APPLICATION_STATUS_COLORS[app.status]}>{APPLICATION_STATUS_LABELS[app.status]}</Badge>
            <InfoHint text={config?.instructions ?? ""} />
            {!!config?.milestone_key && (
              <span className="flex items-center gap-1 text-[11px] font-normal text-[var(--color-text-muted)]">
                kamień: <b>{MILESTONE_LABELS[config.milestone_key] ?? config.milestone_key.toUpperCase()}</b>
                <InfoHint text="Ten typ wniosku zasila kamień na Tablicy procesu: złożenie i zakończenie pojawiają się tam jako propozycje auto-postępu (do zatwierdzenia jednym kliknięciem, nigdy po cichu)." />
              </span>
            )}
          </span>
        }
        subtitle={`${app.type_label} · ${app.project_name}${app.recipient_name ? ` · ${app.recipient_name}` : ""}`}
        backTo={`/power-projects/${app.project_id}?tab=${config?.recipient_mode === "negotiator" ? "wpw" : "wnioski"}`}
        backLabel={config?.recipient_mode === "negotiator" ? "WPW projektu" : "Wnioski projektu"}
        actions={
          editable ? (
            <Button size="sm" variant="secondary"
                    onClick={() => m.cancel.mutate(app.id)}>
              <Ban size={13} className="mr-1" /> Anuluj
            </Button>
          ) : app.status === "submitted" && app.can_manage ? (
            <Button size="sm" variant="secondary" loading={m.complete.isPending}
                    onClick={() => {
                      // Otwarta sprawa zamyka się razem z pakietem — z rozstrzygnięciem
                      // wskazanym przez człowieka (pozytywne zabezpiecza właścicieli).
                      // Wniosek o klauzulę pyta zawsze: pozytywne = stempel na decyzji.
                      if (app.proceeding_info?.status === "open" || app.application_type === "klauzula") {
                        setCompleteOpen(true);
                      } else {
                        m.complete.mutate({ id: app.id });
                      }
                    }}>
              <CheckCircle2 size={13} className="mr-1" /> Zakończ (odpowiedź wpłynęła)
            </Button>
          ) : undefined
        }
      />

      {/* Autor wniosku; po jego odejściu (konto nieaktywne) wniosek prowadzi osoba
          odpowiedzialna z uprawnieniami autora — backend: dlc_01 takeover.py. */}
      <div className="text-xs text-[var(--color-text-secondary)] space-y-0.5">
        <div>
          Autor: <span className="font-medium">{app.created_by_name || "—"}</span>
        </div>
        {app.taken_over_by_name && (
          <div>
            Przejęte przez: <span className="font-medium">{app.taken_over_by_name}</span>
            <span className="text-[var(--color-text-muted)]"> (autor nieaktywny)</span>
          </div>
        )}
      </div>

      {/* oś faz */}
      <div className="flex flex-wrap items-center gap-2">
        {app.phases.map((p, i) => (
          <div key={p.key} className="flex items-center gap-2">
            {i > 0 && <div className="w-5 h-px bg-[var(--color-border-strong)]" />}
            <div className={clsx(
              "px-2 py-1 text-[11px] font-mono uppercase border",
              p.state === "approved"
                ? "bg-green-50 text-green-800 border-green-300"
                : "bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] border-[var(--color-border)]",
            )}>
              {p.label}
            </div>
          </div>
        ))}
      </div>

      {app.status === "waiting_fee" && (
        <div className="text-xs border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
          Wniosek czeka na opłatę skarbową — gdy w opłacie w /official-fees pojawi się potwierdzenie
          przelewu, wróci tu automatycznie (dostaniesz powiadomienie). Do tego czasu nie skompilujesz
          ani nie wyślesz pakietu.
        </div>
      )}

      {/* ── FAZA: zakres ── */}
      <PhaseCard
        app={app} phase="scope" onApprove={() => approve("scope")} onReopen={() => reopen("scope")}
        error={phaseErrors.scope}
        hint="Co obejmuje wniosek i do kogo idzie. Działki podpinają się automatycznie z punktu zaczepienia (np. wszystkie działki urzędu-właściciela); możesz je edytować dopóki faza nie jest zatwierdzona. Dane adresata pochodzą z rejestru urzędów — braki uzupełnisz tam (albo AI-wyszukiwaniem z modułu Wypisy)."
      >
        <div className="space-y-2 text-xs">
          {/* Edycja przed zatwierdzeniem fazy — autor / dyrektor / CEO (can_edit).
              Po zatwierdzeniu drogą jest „Cofnij do edycji" na karcie fazy. */}
          {editable && app.can_edit && !scopeApproved && (
            <div>
              <Button size="sm" variant="secondary" onClick={() => setEditOpen(true)}>
                <Pencil size={13} className="mr-1" /> Edytuj wniosek
              </Button>
            </div>
          )}
          {/* Pełne dane adresata wprost (nie sama nazwa). */}
          {app.recipient_office_detail ? (
            <div className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2 space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <b>Adresat:</b> {app.recipient_office_detail.name}
                {app.recipient_office_detail.kind_display && (
                  <span className="text-[var(--color-text-muted)]">({app.recipient_office_detail.kind_display})</span>
                )}
              </div>
              {app.recipient_office_detail.department && (
                <div className="text-[var(--color-text-secondary)]">Wydział: {app.recipient_office_detail.department}</div>
              )}
              <div className="text-[var(--color-text-secondary)]">
                {app.recipient_office_detail.full_address || "— brak adresu w rejestrze urzędów (uzupełnij w module Wypisy / rejestrze)"}
              </div>
              {(app.recipient_office_detail.phone || app.recipient_office_detail.email || app.recipient_office_detail.contact_person) && (
                <div className="flex flex-wrap gap-x-3 text-[var(--color-text-secondary)]">
                  {app.recipient_office_detail.contact_person && <span>kontakt: {app.recipient_office_detail.contact_person}</span>}
                  {app.recipient_office_detail.phone && <span>tel. {app.recipient_office_detail.phone}</span>}
                  {app.recipient_office_detail.email && <span>{app.recipient_office_detail.email}</span>}
                </div>
              )}
            </div>
          ) : (
            <div><b>Adresat:</b> {app.recipient_name || "—"}</div>
          )}
          <div>
            {app.decision_detail && <><b>Decyzja:</b> <span className="font-mono">{app.decision_detail.sign}</span> ({app.decision_detail.finality_display})</>}
            {app.wpw_parcel_teryt && <> · <b>Działka WPW:</b> <span className="font-mono">{app.wpw_parcel_teryt}</span></>}
            {app.commune && <> · <b>Gmina:</b> {app.commune}</>}
            {app.county && <> · <b>Powiat:</b> {app.county}</>}
          </div>
          {app.parcels_brief.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {app.parcels_brief.map((p) => (
                <span key={p.id} className="px-2 py-0.5 text-[11px] font-mono border border-[var(--color-border)] bg-[var(--color-bg-elevated)]"
                      title={`${p.project_name} · użytek ${p.land_use || "—"} · klasa ${p.soil_class || "—"}`}>
                  {p.teryt}
                </span>
              ))}
            </div>
          )}
          {/* Podgląd zakresu: wystylowane obiekty projektu, działki wniosku na
              niebiesko, pozostałe działki projektu na szaro. */}
          <div className="space-y-1">
            {app.scope_map_png?.url && (
              <a href={app.scope_map_png.url} target="_blank" rel="noreferrer" title="Otwórz PNG w nowej karcie">
                <img
                  src={app.scope_map_png.url}
                  alt="Mapa zakresu wniosku — działki wniosku na niebiesko"
                  className="max-w-full max-h-96 border border-[var(--color-border)]"
                />
              </a>
            )}
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                loading={m.generateScopeMap.isPending}
                title="Wygeneruj PNG: obiekty projektu w stylach mapy, działki wniosku na niebiesko, pozostałe na szaro"
                onClick={() => {
                  setErr("scope", "");
                  setScopeWarnings([]);
                  m.generateScopeMap.mutate(app.id, {
                    onSuccess: (d) => setScopeWarnings(d.scope_map_warnings ?? []),
                    onError: (e) => setErr("scope", apiError(e)),
                  });
                }}
              >
                <MapIcon size={13} className="mr-1" />
                {app.scope_map_png ? "Odśwież podgląd mapy (PNG)" : "Generuj podgląd mapy (PNG)"}
              </Button>
              {app.scope_map_generated_at && (
                <span className="text-[10px] text-[var(--color-text-muted)]">
                  wygenerowano {fmtDateTime(app.scope_map_generated_at)}
                </span>
              )}
            </div>
            {phaseErrors.scope && <div className="text-xs text-red-600">{phaseErrors.scope}</div>}
            {scopeWarnings.map((w, i) => (
              <div key={i} className="text-xs text-amber-600 flex items-start gap-1">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {w}
              </div>
            ))}
          </div>
          {/* Klauzula: liczba egzemplarzy ZAŚWIADCZENIA (typ nie ma fazy Map, a od niej
              liczy się opłata 17 zł/egz.) + obieg ORYGINAŁU decyzji (wyszedł / wrócił). */}
          {app.application_type === "klauzula" && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-1">
                  Egzemplarze zaświadczenia:
                  <input
                    type="number" min={1} max={10}
                    className="input-base !w-16 !py-0.5"
                    value={app.copies}
                    disabled={!editable || scopeApproved}
                    onChange={(e) => m.update.mutate({ id: app.id, payload: { copies: Number(e.target.value) } })}
                  />
                </label>
                <span className="text-[var(--color-text-muted)]">
                  opłata skarbowa 17 zł/egz. → <b>{app.application_fee_info?.amount_default ?? "—"} zł</b>{" "}
                  (zlecasz ją w fazie „Osoba i pełnomocnictwa”, do organu-adresata)
                </span>
                <InfoHint text="Zaświadczenie o ostateczności kosztuje 17 zł opłaty skarbowej za każdy egzemplarz — osobno od 17 zł za pełnomocnictwo. Zwykle wystarczy jeden. Liczbę ustal przed zatwierdzeniem zakresu: od niej liczy się kwota opłaty od wniosku." />
              </div>
              <DecisionOriginalCard
                app={app}
                editable={app.can_manage}
                pending={m.decisionOriginal.isPending}
                error={m.decisionOriginal.error ? apiError(m.decisionOriginal.error) : null}
                onSave={(v) => m.decisionOriginal.mutate({ id: app.id, ...v })}
              />
            </div>
          )}
          {/* ULICP: teren inwestycji z pasa wokół sieci — powierzchnia do druku i obrys
              na mapę. Szerokość pasa zmienia się TU, patrząc na mapę. */}
          {app.application_type === "ulicp" && (
            <UlicpAreaCard
              applicationId={app.id}
              disabled={["submitted", "completed", "cancelled"].includes(app.status)}
            />
          )}
          {/* Regulacja: pas służebności przesyłu wg wytycznych TAURON — powierzchnia
              do pisma i obrys na mapę oraz do skryptu CAD. Wymiary stacji i złączy,
              których nie niesie rysunek, wpisuje się TU, patrząc na mapę. */}
          {app.application_type === "regulacja" && (
            <RegulacjaAreaCard
              applicationId={app.id}
              disabled={["submitted", "completed", "cancelled"].includes(app.status)}
            />
          )}
          {/* Strony wypisu podpięte do działek wniosku — pobranie tylko właściwych stron. */}
          {app.parcels_brief.some((p) => p.wypisy.length > 0) && (
            <div className="space-y-1">
              <div className="section-label">Wypisy działek (PDF ze starostwa)</div>
              {app.parcels_brief.filter((p) => p.wypisy.length > 0).map((p) => (
                <div key={p.id} className="flex items-center gap-2 flex-wrap text-[11px]">
                  <span className="font-mono">{p.teryt}</span>
                  {p.wypisy.map((w) => (
                    <span key={w.id} className="inline-flex items-center gap-2">
                      {w.pages && (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
                          onClick={() => downloadWnioskWypisPages(app.id, w.id, p.id)}
                          title="Pobierz tylko strony obejmujące tę działkę"
                        >
                          <Download size={11} /> strony {w.pages}
                        </button>
                      )}
                      {w.file?.url && (
                        <a
                          href={w.file.url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[var(--color-text-muted)] hover:underline"
                          title={w.file.original_filename}
                        >
                          pełny PDF{w.page_count ? ` (${w.page_count} str.)` : ""}
                        </a>
                      )}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}
          {app.application_type === "odrolnienie" && (
            <div className="space-y-1">
              {(() => {
                const inScope = new Set(app.parcels_brief.map((p) => p.teryt));
                const group = (scopeOverview?.odrolnienie_scope ?? []).find((g) =>
                  g.parcels.some((p) => inScope.has(p.teryt)),
                );
                if (!group) return null;
                return (
                  <div className="mb-3">
                    <div className="section-label flex items-center gap-1">
                      Preselekcja działek — obręb {group.obreb_label}
                      <InfoHint text="Co automat wziął do wniosku i czego nie wziął. Reguła: wyłączenia wymaga tylko grunt TRWALE zajęty przez obiekt naziemny (stacja / złącze / słup) na klasie chronionej I–III; sam kabel pod ziemią nie. Widok informacyjny — zakres ustala się przy zakładaniu wniosku." />
                    </div>
                    <div className="mt-1 border border-[var(--color-border)]">
                      {group.parcels.map((p) => {
                        const taken = inScope.has(p.teryt);
                        return (
                          <div
                            key={p.id}
                            className={`flex items-start gap-2 px-2 py-1 text-xs border-b border-[var(--color-border)] last:border-b-0 ${
                              taken ? "" : "opacity-60"
                            }`}
                          >
                            <Badge color={taken ? "green" : "gray"}>
                              {taken ? "w zakresie" : "pominięta"}
                            </Badge>
                            <span className="flex-1">
                              <span className="font-mono">{p.short_number || p.teryt}</span>
                              {p.exclusion_area_m2 > 0 && (
                                <span className="ml-2 font-mono text-[var(--color-text-muted)]">
                                  {p.exclusion_area_m2} m² · {p.devices} urz.
                                </span>
                              )}
                              <span className="block text-[var(--color-text-muted)]">
                                {p.reason}
                                {p.source_label && ` (źródło: ${p.source_label})`}
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <div className="section-label flex items-center gap-1">
                Kwalifikacja działek (automat)
                <InfoHint text="Reguła (zweryfikowana): wyłączenia wymaga tylko powierzchnia TRWALE zajęta (stacja trafo / złącze kablowe / słup z obiektów mapy trasy) na użytku rolnym klasy chronionej I–III. Kabel podziemny poniżej warstwy uprawnej NIE wymaga wyłączenia. Każdy wynik ma uzasadnienie — możesz go ręcznie zmienić (przełącznik), a automat możesz przeliczyć ponownie po zmianie danych działek." />
              </div>
              {app.qualification.map((q) => (
                <div key={q.parcel_id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={q.effective_required}
                    disabled={!editable}
                    onChange={() =>
                      m.setQualification.mutate({
                        id: app.id,
                        overrides: [{
                          parcel_id: q.parcel_id,
                          override: q.effective_required ? false
                            : true,
                        }],
                      })
                    }
                  />
                  <span className="font-mono">{q.teryt}</span>
                  {q.override !== null && <Badge color="purple">ręcznie</Badge>}
                  {q.manual && q.override === null && <Badge color="yellow">do decyzji</Badge>}
                  {/* Skąd wzięła się klasa: odczyt punktowy spod urządzenia czy wykaz całej
                      działki. Bez tego nie widać, czy werdykt w ogóle rozstrzyga sprawę. */}
                  {q.source?.method_label && (
                    <Badge color={q.source.method === "county_api" ? "green"
                      : q.source.method === "map_ai" ? "blue"
                        : q.source.method === "manual" ? "purple" : "gray"}>
                      {q.source.method_label}
                    </Badge>
                  )}
                  <span
                    className="text-[var(--color-text-muted)] truncate"
                    title={q.source?.why ? `${q.reason}\n\n${q.source.why}` : q.reason}
                  >
                    {q.reason}
                  </span>
                  {/* Powierzchnię i klasę liczy backend (stawki × urządzenia, odczyt
                      punktowy klasy). Te pola to KOREKTA na wypadek obmiaru z terenu
                      albo klasy z wypisu, którego jeszcze nie wgrano — puste przywraca
                      wartość wyliczoną. */}
                  <QualificationOverrides app={app} row={q} editable={editable} mutate={m.setQualification} />
                </div>
              ))}
            </div>
          )}
        </div>
      </PhaseCard>

      {/* ── FAZA: osoba i pełnomocnictwa ── */}
      {app.phases.some((p) => p.key === "poa") && (
        <PhaseCard
          app={app} phase="poa" onApprove={() => approve("poa")} onReopen={() => reopen("poa")}
          error={phaseErrors.poa}
          errorAction={poaUnpaidFeeOnly && editable ? (
            <Button size="sm" variant="secondary" onClick={() => setUnpaidFeeApproveConfirm(true)}>
              Zatwierdź bez potwierdzenia opłaty…
            </Button>
          ) : undefined}
          hint={`Wybierz osobę odpowiedzialną — system sam podepnie jej WAŻNE pełnomocnictwo od właściwego mocodawcy (rejestr pełnomocnictw), dopasowane do ODDZIAŁU projektu (wpis dla innego oddziału nigdy nie podepnie się automatycznie; przy kilku pasujących wygrywa najdłużej ważne), a gdy urząd-adresat wymaga pełnego łańcucha — także drugie (zarząd → pracownik klienta). Możesz też podpiąć wpis RĘCZNIE („Podepnij ręcznie…”) — ręcznych auto-podpinanie nie ruszy — oraz odpiąć każde błędne. Każde ogniwo wymaga opłaty skarbowej 17 zł — zawsze do urzędu siedziby MOCODAWCY (podmiot z pola 1 wniosku), nigdy do urzędu-adresata (pole 3); tu: ${app.stamp_duty_office.office_name || "NIE ROZPOZNANO — wskaż urząd w oknie opłaty"}. W oknie opłaty można ten urząd poprawić ręcznie. Opłatę podpinasz istniejącą (opłaconą — lista pokazuje datę opłacenia — albo jeszcze nieopłaconą) lub prosisz o nową — przycisk „Opłata 17 zł”. Przy opłacie bez potwierdzenia przelewu wniosek czeka jako szkic, aż potwierdzenie pojawi się w /official-fees (to ono jedzie do urzędu w pakiecie; osobnym plikiem obok tylko wtedy, gdy samo ma podpis elektroniczny). Samą fazę można mimo to zatwierdzić świadomie („Zatwierdź bez potwierdzenia opłaty” pod komunikatem błędu), żeby szykować dokument, mapy i załączniki — a nawet skompilować i wysłać pakiet, wtedy jednak bez opłaty (z ostrzeżeniem; po przelewie skompiluj ponownie, a wysłanemu wnioskowi dośl potwierdzenie); gdy opłata zostanie odrzucona albo wycofana, faza wróci do edycji. Pełnomocnictwo podpisane elektronicznie (PAdES) NIE wchodzi do scalenia — system wykrywa podpis w pliku i wysyła je jako osobny plik obok pakietu, żeby podpis pozostał ważny. Gdy opłata w danym przypadku nie jest należna, użyj przycisku „Niepotrzebna” przy ogniwie (wymaga uzasadnienia); gdy opłaty całego wniosku poszły poza systemem, zaznacz checkbox nad łańcuchem. Ostrzeżemy, gdy pełnomocnictwo wygasa przed terminem KPA.${
            app.application_fee_info
              ? ` Ten wniosek ma DRUGĄ opłatę skarbową — od samego wniosku (${app.application_fee_info.amount_default} zł): płaci się ją do urzędu-ADRESATA (organ wydający decyzję), a nie do urzędu mocodawcy. Zlecasz ją na osobnej karcie nad łańcuchem, tymi samymi trybami (nowa / istniejąca / niepotrzebna z uzasadnieniem); kwota jest edytowalna, bo stawka zależy od zakresu wniosku. Bez rozliczenia OBU opłat wniosek nie stanie się gotowy do wysyłki.`
              : ""
          }`}
        >
          <div className="space-y-2 text-xs">
            <div className="grid grid-cols-2 gap-3 items-end">
              <label className="block">
                <span className="section-label">Osoba odpowiedzialna</span>
                <SearchableSelect
                  options={userOptions}
                  value={app.responsible_user ? String(app.responsible_user) : ""}
                  placeholder="Wybierz osobę…"
                  disabled={!editable}
                  onChange={(e) => m.update.mutate({ id: app.id, payload: { responsible_user: e.target.value } })}
                />
              </label>
              <div className="flex gap-2">
                <Button
                  size="sm" variant="secondary"
                  loading={m.resolvePoa.isPending}
                  onClick={() => {
                    setErr("poa", "");
                    setPoaWarnings([]);
                    setPoaSummary("");
                    m.resolvePoa.mutate(app.id, {
                      onSuccess: (data) => {
                        setPoaWarnings(data.poa_report?.warnings ?? []);
                        setPoaSummary(data.poa_report?.summary ?? "");
                      },
                      onError: (e) => setErr("poa", apiError(e)),
                    });
                  }}
                >
                  Auto-podepnij pełnomocnictwa
                </Button>
                {editable && (
                  <Button size="sm" variant="secondary" onClick={() => setPoaPickerOpen((v) => !v)}>
                    {poaPickerOpen ? "Zamknij wybór" : "Podepnij ręcznie…"}
                  </Button>
                )}
              </div>
            </div>
            {poaPickerOpen && editable && (
              <div className="border border-[var(--color-border)] p-2 space-y-1">
                <div className="section-label">Rejestr pełnomocnictw — wybierz wpis do podpięcia</div>
                {!poaCandidates ? (
                  <Spinner size="sm" />
                ) : poaCandidates.length === 0 ? (
                  <EmptyState>Brak ważnych pełnomocnictw do podpięcia — dodaj wpis w rejestrze.</EmptyState>
                ) : (
                  poaCandidates.map((cand) => (
                    <div key={cand.id} className="flex items-center gap-2 border-b border-[var(--color-border)] last:border-b-0 py-1">
                      <span className="flex-1">
                        <b>{cand.principal_display}</b> → {cand.attorney_display}
                        {cand.valid_until ? ` (do ${fmtDate(cand.valid_until)})` : " (bezterminowe)"}
                        {cand.match === "branch" && <Badge color="green">oddział projektu</Badge>}
                        {cand.match === "project" && <Badge color="green">to przedsięwzięcie</Badge>}
                        {cand.match === "other_scope" && (
                          <Badge color="yellow">
                            inny zakres: {cand.eop_branch_name || cand.project_name}
                          </Badge>
                        )}
                        {cand.validity === "expiring" && <Badge color="yellow">wygasa</Badge>}
                        {!cand.file && <Badge color="red">brak skanu</Badge>}
                      </span>
                      <Button
                        size="sm" variant="secondary"
                        loading={m.attachPoa.isPending}
                        onClick={() => {
                          setErr("poa", "");
                          m.attachPoa.mutate(
                            { id: app.id, poaId: cand.id },
                            { onError: (e) => setErr("poa", apiError(e)) },
                          );
                        }}
                      >
                        Podepnij
                      </Button>
                    </div>
                  ))
                )}
              </div>
            )}
            {/* Opłata skarbowa OD SAMEGO WNIOSKU (ULICP: 598 zł) — obok 17 zł od
                pełnomocnictwa. Idzie do INNEGO urzędu (adresat wniosku) i dlatego ma
                własną kartę, nad łańcuchem pełnomocnictw. */}
            {app.application_fee_info && (
              <ApplicationFeeCard
                app={app}
                info={app.application_fee_info}
                editable={editable}
                onOpen={(mode) => setAppFeeModal({ mode })}
              />
            )}
            {/* Nadrzędny przełącznik: opłaty rozliczone poza systemem (wstecznie / przy okienku) */}
            <label
              className={clsx(
                "flex items-start gap-2 p-2 border transition-colors",
                app.poa_fees_settled_externally
                  ? "border-amber-400 bg-amber-50 text-amber-900"
                  : "border-[var(--color-border)]",
                editable ? "cursor-pointer" : "opacity-60 cursor-not-allowed",
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={app.poa_fees_settled_externally}
                disabled={!editable || m.update.isPending}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSettleFeesConfirm(true); // potwierdzenie zamiast natychmiastowego zapisu
                  } else {
                    m.update.mutate({ id: app.id, payload: { poa_fees_settled_externally: false } });
                  }
                }}
              />
              <span>
                <b>Opłaty skarbowe rozliczone poza systemem</b>
                <span className="block text-[var(--color-text-muted)]">
                  Wniosek wprowadzany wstecznie / opłata wniesiona poza systemem — zdejmuje wymóg
                  opłaty 17 zł przy każdym pełnomocnictwie{app.application_fee_info ? " ORAZ opłaty od samego wniosku" : ""}.
                  Skany (pełnomocnictwo + potwierdzenia opłat) wgraj w fazie „Załączniki".
                </span>
              </span>
            </label>
            {poaSummary && (
              <div className="flex items-center gap-1 text-green-700">
                <CheckCircle2 size={12} /> {poaSummary}
              </div>
            )}
            {poaWarnings.map((w, i) => (
              <div key={i} className="text-amber-700">⚠ {w}</div>
            ))}
            {app.poas.length === 0 ? (
              <EmptyState>Brak podpiętych pełnomocnictw</EmptyState>
            ) : (
              app.poas.map((link) => (
                <div key={link.id} className="border border-[var(--color-border)] p-2 flex items-center gap-2">
                  <span className="flex-1">
                    <b>{link.poa.principal_display}</b> → {link.poa.attorney_display}
                    {link.poa.valid_until ? ` (do ${fmtDate(link.poa.valid_until)})` : " (bezterminowe)"}
                    {link.manual && <Badge color="blue">ręczne</Badge>}
                    {link.poa.validity === "expiring" && <Badge color="yellow">wygasa</Badge>}
                    {!link.poa.file && <Badge color="red">brak skanu</Badge>}
                  </span>
                  {app.poa_fees_settled_externally ? (
                    <Badge color="gray">opłata poza systemem</Badge>
                  ) : link.fee_not_required ? (
                    <>
                      <Badge color="gray">opłata niepotrzebna: {link.fee_not_required_reason}</Badge>
                      {editable && (
                        <Button
                          size="sm" variant="ghost"
                          title="Zmień decyzję — popraw uzasadnienie albo jednak rozlicz opłatę"
                          onClick={() => setFeeModalLink({ linkId: link.id, mode: "not_required" })}
                        >
                          Zmień
                        </Button>
                      )}
                    </>
                  ) : link.fee ? (
                    <>
                      <Badge
                        color={
                          ["payment_uploaded", "acknowledged"].includes(link.fee_status ?? "")
                            ? "green"
                            : ["rejected", "withdrawn"].includes(link.fee_status ?? "")
                              ? "red"
                              : "yellow"
                        }
                      >
                        17 zł: {FEE_LABELS[link.fee_status ?? ""] ?? link.fee_status}
                        {link.fee_paid_at ? ` ${fmtDate(link.fee_paid_at)}` : ""}
                      </Badge>
                      {UNPAID_FEE_STATUSES.includes(link.fee_status ?? "") && !app.poa_fees_settled_externally && (
                        <span className="text-[var(--color-text-muted)]">
                          bez przelewu: kompilacja i wysyłka tylko świadomie (bez opłaty)
                        </span>
                      )}
                      {/* Odrzucona/wycofana opłata to ślepy zaułek: bez tego przycisku ogniwo
                          zostawało z martwą opłatą i nie dało się ani poprosić o nową, ani
                          oznaczyć „niepotrzebna". */}
                      {editable && ["rejected", "withdrawn"].includes(link.fee_status ?? "") && (
                        <Button
                          size="sm" variant="secondary"
                          title="Poproś o nową opłatę, podepnij inną albo oznacz jako niepotrzebną"
                          onClick={() => setFeeModalLink({ linkId: link.id })}
                        >
                          <Wallet size={12} className="mr-1" /> Rozlicz ponownie
                        </Button>
                      )}
                    </>
                  ) : editable ? (
                    <>
                      <Button
                        size="sm" variant="secondary"
                        onClick={() => setFeeModalLink({ linkId: link.id })}
                      >
                        <Wallet size={12} className="mr-1" /> Opłata 17 zł
                      </Button>
                      {/* „Niepotrzebna" wprost przy ogniwie: bramka fazy obiecuje tę opcję, a była
                          schowana w modalu za przyciskiem opłaty — zgłoszenie z 21.08.2026. */}
                      <Button
                        size="sm" variant="ghost"
                        title="Opłata skarbowa nie jest wymagana przy tym pełnomocnictwie (podaj uzasadnienie)"
                        onClick={() => setFeeModalLink({ linkId: link.id, mode: "not_required" })}
                      >
                        Niepotrzebna
                      </Button>
                    </>
                  ) : null}
                  {editable && (
                    <Button
                      size="sm" variant="ghost"
                      loading={m.detachPoa.isPending}
                      onClick={() => {
                        setErr("poa", "");
                        m.detachPoa.mutate(
                          { id: app.id, linkId: link.id },
                          { onError: (e) => setErr("poa", apiError(e)) },
                        );
                      }}
                    >
                      Odepnij
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </PhaseCard>
      )}

      {/* ── FAZA: dokument ── */}
      <PhaseCard
        app={app} phase="document" onApprove={() => approve("document")} onReopen={() => reopen("document")}
        error={phaseErrors.document}
        hint="Pismo wniosku: wygeneruj z szablonu ODT (silnik #zmiennych# — dane urzędu, działek, osoby i pełnomocnictw wchodzą same; pojedyncze pola możesz nadpisać przed generacją) ALBO wgraj własny gotowy plik (wygrywa nad generatem; PDF pozwoli skleić cały pakiet w jeden plik). Szablony podmienia się w Generacji dokumentów bez zmian w kodzie."
      >
        <div className="space-y-2 text-xs">
          {/* Wniosek uniwersalny: nie ma „druku typu" — pismem jest wskazany szablon.
              Bez tej linii na karcie nie widać, czym w ogóle jest ten wniosek. */}
          {app.config?.template_pick && (
            <div className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2">
              <b>Szablon pisma:</b>{" "}
              {app.doc_template_name || (
                <span className="text-amber-700 dark:text-amber-400">
                  nie wskazano — wybierz szablon w edycji wniosku albo w oknie generowania
                </span>
              )}
            </div>
          )}
          {/* Pakiet WPW: działka z budową I demontażem potrzebuje 4a + 4b (a przy
              przebudowie 4c). Wcześniej trzeba było wchodzić w „Generuj z szablonu"
              raz na każdy druk, a historia oznaczała jako „obowiązujący" tylko
              ostatni — stąd ręczne sklejanie PDF-ów. Tu widać komplet i robi się go
              jednym kliknięciem, jako JEDEN plik. */}
          {kwWarningNotice}
          {app.wpw_forms_state && app.wpw_forms_state.kinds.length > 0 && (
            <div className="border border-[var(--color-border)] p-2 space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold">
                  Formularze zgody z mapy{" "}
                  <span className="font-normal text-[var(--color-text-muted)]">
                    ({app.wpw_forms_state.kinds.length})
                  </span>
                </span>
                <Button
                  size="sm" variant="secondary"
                  loading={m.generateWpwDescriptions.isPending}
                  disabled={!editable}
                  title="Układa opis urządzeń dla każdego formularza z rozbicia mapy działki — nadpisuje pola poniżej, pozostają edytowalne"
                  onClick={() => {
                    setWpwFormsErr("");
                    setWpwDescDrafts({});
                    m.generateWpwDescriptions.mutate(app.id, {
                      // Kubełki, których AI nie ułożyło, wracają w ai_errors —
                      // wcześniej znikały po cichu i pole zostawało puste bez słowa
                      // wyjaśnienia, co wyglądało jak „AI nie działa".
                      onSuccess: (r) =>
                        setWpwFormsErr(
                          (r.ai_errors ?? []).length
                            ? `Opis AI niepełny — ${(r.ai_errors ?? []).join("; ")}. ` +
                              "Uzupełnij brakujące pola ręcznie."
                            : "",
                        ),
                      onError: (e) => setWpwFormsErr(apiError(e)),
                    });
                  }}
                >
                  <Sparkles size={13} className="mr-1" /> Ułóż opisy AI
                </Button>
              </div>
              {/* Opis urządzeń PER FORMULARZ (kubełek statusu działki) — druk likwidacji
                  dostaje własną frazę, druk budowy własną. To, co stoi w polu, wchodzi
                  do druku 1:1; puste pole = druk wyjdzie z pustym zdaniem „w postaci: ”
                  (generacja tego nie blokuje, ale ostrzega). */}
              <ul className="space-y-1">
                {app.wpw_forms_state.kinds.map((k) => (
                  <li key={k.kind} className="space-y-0.5">
                    <div className="flex items-center gap-1">
                      <span className={k.generated ? "text-green-700 dark:text-green-500" : "text-[var(--color-text-muted)]"}>
                        {k.generated ? "✓" : "—"}
                      </span>
                      <span>{k.label}</span>
                      {k.generated && !k.in_batch && (
                        <span className="text-[10px] font-mono uppercase text-[var(--color-text-muted)]">
                          osobno
                        </span>
                      )}
                      {k.stale && app.wpw_forms_state?.batch && (
                        <span
                          className="text-[10px] font-mono uppercase text-amber-700 dark:text-amber-400"
                          title="Komplet ma inny opis tego formularza niż pole poniżej"
                        >
                          komplet nieaktualny
                        </span>
                      )}
                    </div>
                    <textarea
                      className="input-base !h-auto w-full !py-1"
                      rows={2}
                      placeholder="Opis urządzeń tego formularza — kliknij „Ułóż opisy AI” albo wpisz ręcznie"
                      value={wpwDescDrafts[k.kind] ?? k.description}
                      disabled={!editable}
                      onChange={(e) =>
                        setWpwDescDrafts((d) => ({ ...d, [k.kind]: e.target.value }))
                      }
                      onBlur={() => {
                        const draft = wpwDescDrafts[k.kind];
                        if (draft === undefined || draft === k.description) return;
                        m.setWpwFormDescriptions.mutate(
                          { id: app.id, descriptions: { [k.kind]: draft } },
                          {
                            onSuccess: () =>
                              setWpwDescDrafts(({ [k.kind]: _saved, ...rest }) => rest),
                            onError: (e) => setWpwFormsErr(apiError(e)),
                          },
                        );
                      }}
                    />
                  </li>
                ))}
              </ul>
              <Button
                size="sm"
                loading={m.generateWpwForms.isPending || wpwFormsRunning}
                disabled={!editable}
                title="Generuje wszystkie dobrane formularze naraz (każdy ze SWOIM opisem powyżej) i skleja je w jeden dokument wniosku"
                onClick={() => withKwWarningConfirm("generuj formularze", () => {
                  setWpwFormsErr("");
                  m.generateWpwForms.mutate(
                    { id: app.id },
                    {
                      onSuccess: (r) => setWpwFormsTask(r.task_id),
                      onError: (e) => setWpwFormsErr(apiError(e)),
                    },
                  );
                })}
              >
                <FileCog size={13} className="mr-1" />
                Generuj wszystkie ({app.wpw_forms_state.kinds.length}) w jednym pliku
              </Button>
              {app.wpw_forms_state.batch && (
                <div className="text-[var(--color-text-muted)]">
                  Komplet z {fmtDateTime(app.wpw_forms_state.batch.generated_at)}:{" "}
                  {app.wpw_forms_state.batch.file?.url ? (
                    <a
                      className="font-mono text-[var(--color-primary)] hover:underline"
                      href={app.wpw_forms_state.batch.file.url}
                      download={app.wpw_forms_state.batch.file.original_filename}
                    >
                      {app.wpw_forms_state.batch.file.original_filename}
                    </a>
                  ) : "plik niedostępny"}
                </div>
              )}
              {app.wpw_forms_state.batch?.stale && !app.wpw_forms_state.own_document_wins && (
                <div className="text-amber-700 dark:text-amber-400">
                  ⚠ Opisy lub formularze zmieniły się po wygenerowaniu kompletu — kompilacja
                  pakietu wygeneruje go ponownie z opisami z pól powyżej (albo kliknij
                  „Generuj wszystkie”, żeby obejrzeć komplet przed kompilacją).
                </div>
              )}
              {wpwFormsRunning && (
                <div className="text-[var(--color-text-muted)]">
                  Generuję formularze w tle (każdy druk to osobna konwersja do PDF)…
                </div>
              )}
              {app.wpw_forms_state.own_document_wins && (
                <div className="text-amber-700 dark:text-amber-400">
                  ⚠ Wgrany własny plik ZASTĘPUJE wygenerowane formularze — do pakietu
                  wejdzie tylko on. Usuń go („usuń (wróć do generatu)"), jeśli pakiet
                  ma zawierać komplet formularzy z systemu.
                </div>
              )}
              {wpwFormsErr && <div className="text-amber-700">⚠ {wpwFormsErr}</div>}
            </div>
          )}
          {app.wpw_forms_state && app.wpw_forms_state.kinds.length > 0 && <OrDivider />}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => setDocModal(true)} disabled={!editable}>
              <FileCog size={13} className="mr-1" /> Generuj z szablonu
            </Button>
          </div>
          <OrDivider />
          <FileDrop
            accept=".pdf,.odt,.docx"
            disabled={!editable}
            busy={m.ownDocument.isPending}
            label="Wgraj plik pdf — przeciągnij tutaj lub kliknij, aby wybrać"
            hint={config?.signed_package
              ? "Własny druk (PDF, ODT lub DOCX) — zastąpi generat w pakiecie. Podpis składasz dopiero na gotowym pakiecie (faza kompilacji)."
              : "Podpisany / własny druk (PDF, ODT lub DOCX) — zastąpi generat w pakiecie."}
            onFiles={(files) => {
              const f = files[0];
              if (f) m.ownDocument.mutate({ id: app.id, file: f });
            }}
          />
          {app.own_document ? (
            <div>
              Własny plik: <a className="font-mono hover:underline" href={app.own_document.url ?? "#"} download>
                {app.own_document.original_filename}
              </a>{" "}
              {app.own_document_pdf?.url && (
                <>
                  <a className="font-mono text-[var(--color-primary)] hover:underline" href={app.own_document_pdf.url}
                     target="_blank" rel="noreferrer" title="PDF po zapisie z edytora — do kontroli">
                    PDF ({app.own_document_pdf.original_filename})
                  </a>{" "}
                </>
              )}
              <button className="text-red-600 hover:underline" disabled={!editable}
                      onClick={() => m.ownDocument.mutate({ id: app.id, file: null })}>
                usuń (wróć do generatu)
              </button>
            </div>
          ) : (
            <div className="text-[var(--color-text-muted)]">
              {config?.signed_package ? (
                <>
                  Brak własnego pliku — do pakietu wejdzie ostatni generat z szablonu. Druku
                  NIE podpisuj na tym etapie: podpis przepadłby przy scalaniu pakietu. Pola
                  wypełniane ręcznie uzupełniasz i podpisujesz na gotowym pakiecie — wgrywasz
                  go w fazie kompilacji („Wgraj podpisany pakiet").
                </>
              ) : (
                <>
                  Brak własnego pliku — użyty zostanie ostatni generat z szablonu. Gotowy druk
                  po wydrukowaniu, uzupełnieniu i podpisaniu wgraj tutaj (PDF/ODT/DOCX) — to on
                  wejdzie do kompilacji pakietu zamiast generatu.
                </>
              )}
            </div>
          )}

          {/* Pismo w edytorze w przeglądarce: pola ręczne `#wpisz_…#` szablonu uzupełnia
              się bez pobierania pliku; każdy zapis = nowa wersja, PDF dopiero po zapisie.
              Dopóki w pliku zostaje znacznik `#…#`, faza i kompilacja są zablokowane. */}
          {(app.document_fill?.length ?? 0) > 0 && (
            <div className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 space-y-2">
              <div className="font-medium">Pismo do pakietu</div>
              {app.document_fill!.map((row) => (
                <div key={`${row.kind}-${row.id}`} className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* PDF bieżącej wersji (z generacji / zapisu w edytorze) to plik do kontroli;
                        ODT zostaje jako źródło do dalszej edycji. */}
                    {row.pdf?.url ? (
                      <a href={row.pdf.url} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 font-mono border border-[var(--color-border-strong)] px-2 py-0.5 hover:bg-[var(--color-bg-base)]"
                         title="PDF bieżącej wersji — do kontroli przed kompilacją">
                        <Download size={12} /> {row.pdf.original_filename}
                      </a>
                    ) : (
                      <span className="font-mono">{row.filename}</span>
                    )}
                    {row.pdf?.url && row.file?.url && (
                      <a href={row.file.url} download className="text-[var(--color-text-muted)] hover:underline"
                         title="Źródło (ODT) — do edycji poza systemem">ODT↓</a>
                    )}
                    <span className="text-[var(--color-text-muted)]">{row.label}</span>
                    {row.editable ? (
                      row.complete
                        ? <Badge color="green">pola uzupełnione</Badge>
                        : <Badge color="red">do uzupełnienia: {row.unfilled.length}</Badge>
                    ) : (
                      <Badge color="gray">gotowy plik — bez edycji</Badge>
                    )}
                    {row.editable && editorAvailable && (
                      <Button
                        size="sm"
                        variant={row.complete ? "secondary" : "primary"}
                        disabled={!editable}
                        onClick={() => setEditorDoc({ kind: row.kind, id: row.id, label: row.label })}
                        title="Otwórz pismo w edytorze w przeglądarce — bez pobierania na dysk"
                      >
                        <FileCog size={13} className="mr-1" /> Edytuj w przeglądarce
                      </Button>
                    )}
                  </div>
                  {row.editable && !row.pdf && row.pdf_error && (
                    <div className="text-red-700 dark:text-red-400">⚠ Brak PDF-a: {row.pdf_error}</div>
                  )}
                  {row.editable && !row.complete && (
                    <div className="text-amber-700 dark:text-amber-400">
                      ⚠ Nieuzupełnione pola:{" "}
                      {row.unfilled.map((n) => <span key={n} className="font-mono mr-1">#{n}#</span>)}
                      — bez tego faza się nie zamknie, a pakiet nie skompiluje.
                      {!editorAvailable && " Edytor w przeglądarce jest wyłączony — pobierz ODT, uzupełnij lokalnie i wgraj jako własny plik."}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Historia wersji generatów — każde generowanie zostawia wiersz z plikiem. */}
          {(docHistory?.length ?? 0) > 0 && (
            <div>
              <button
                type="button"
                className="text-[var(--color-text-muted)] hover:underline"
                onClick={() => setDocHistoryOpen((v) => !v)}
              >
                {docHistoryOpen ? "▾" : "▸"} Historia generatów ({docHistory!.length})
              </button>
              {docHistoryOpen && (
                <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                  {docHistory!.map((d, i) => (
                    <li key={d.id} className="flex flex-wrap items-center gap-2">
                      <span className="text-[var(--color-text-muted)] font-mono">
                        {d.generated_at ? fmtDateTime(d.generated_at) : "—"}
                      </span>
                      <span>{d.template_name || "szablon usunięty"}</span>
                      {i === 0 && d.status === "success" && (
                        <Badge color="green">obowiązujący</Badge>
                      )}
                      {d.status === "failure" && <Badge color="red">błąd</Badge>}
                      {d.pdf_file?.url && (
                        <a
                          className="font-mono text-[var(--color-primary)] hover:underline"
                          href={d.pdf_file.url}
                          target="_blank"
                          rel="noreferrer"
                          title="PDF tej wersji"
                        >
                          {d.pdf_file.original_filename}
                        </a>
                      )}
                      {d.file?.url ? (
                        <a
                          className={`font-mono hover:underline ${d.pdf_file?.url ? "text-[var(--color-text-muted)]" : "text-[var(--color-primary)]"}`}
                          href={d.file.url}
                          download={d.file.original_filename}
                          title="Źródło (ODT)"
                        >
                          {d.pdf_file?.url ? "ODT↓" : d.file.original_filename}
                        </a>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">bez pliku</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* W druku zaznaczamy TYLKO te załączniki, które faktycznie mamy — brak
              krzyżyka ma być widoczny tu, zanim ktoś wyśle niekompletny wniosek. */}
          {app.application_type === "odrolnienie" && (() => {
            const marked = app.checklist.filter((i) => i.satisfied).map((i) => i.label);
            const missing = app.checklist.filter((i) => i.required && !i.satisfied).map((i) => i.label);
            if (missing.length === 0) return null;
            return (
              <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs">
                <b>⚠ Nie wszystkie załączniki są skompletowane.</b> W wygenerowanym druku
                krzyżyk stoi tylko przy tych, które faktycznie dołączamy
                {marked.length > 0 ? ` (${marked.join(", ")})` : " — na razie przy żadnym"}.
                <div className="mt-1 text-[var(--color-text-muted)]">
                  Brakuje: {missing.join(", ")}. Uzupełnij w fazie „Załączniki" i przegeneruj pismo.
                </div>
              </div>
            );
          })()}

          {/* Wspólny opis urządzeń ENERGA dla wszystkich działek wniosku —
              zmienna #dzialki_jst_opis_prac_energa_ai# w szablonie ODT.
              Odrolnienie ma własny opis (#odrolnienie_opis_prac#) liczony przez AI
              w oknie generowania, więc to pole byłoby tam tylko myląco pustym polem
              do wypełnienia — nie pokazujemy go. */}
          {/* ULICP: parametry obiektu z Załącznika C druku. AI proponuje je z wykazu
              urządzeń mapy (powierzchnia to grunt TRWALE zajęty przez fundamenty, a nie
              długość kabla), projektant poprawia — i to jego wartości idą do druku. */}
          {app.application_type === "ulicp" && (
            <UlicpObjectParams app={app} editable={editable} />
          )}
          {/* GDDKiA: dane drogi krajowej (numer, kilometraż, strona) do druku
              „zezwolenie na lokalizację w pasie drogowym" — API mapy GDDKiA
              proponuje, człowiek poprawia; + przejście na stronę mapy GDDKiA. */}
          {app.proposed_doc_template_kind === "app_lokalizacyjny_gddkia" && (
            <RoadDataCard
              app={app}
              editable={editable}
              computing={m.computeRoadData.isPending}
              suggesting={m.suggestRelacja.isPending}
              error={roadErr}
              onCompute={() => {
                setRoadErr("");
                m.computeRoadData.mutate(app.id, { onError: (e) => setRoadErr(apiError(e)) });
              }}
              onSuggestRelacja={() => {
                setRoadErr("");
                m.suggestRelacja.mutate(app.id, { onError: (e) => setRoadErr(apiError(e)) });
              }}
              onSave={(roadData) => {
                setRoadErr("");
                m.update.mutate(
                  { id: app.id, payload: { road_data: roadData } },
                  { onError: (e) => setRoadErr(apiError(e)) },
                );
              }}
            />
          )}
          {/* Opis prac ENERGA zasila #dzialki_jst_opis_prac_energa_ai# — zmienną
              formularzy ENERGA. ULICP i odrolnienie mają własne opisy liczone AI
              w oknie generowania, a regulacja (TAURON/OMI) ma opisy urządzeń „pod klucz"
              w karcie pasa — tutaj byłoby to tylko myląco puste pole. Pakiet WPW
              z dobranymi formularzami ma opisy PER RODZAJ w karcie „Formularze zgody
              z mapy" powyżej — wspólne pole by z nimi konkurowało. */}
          {!["odrolnienie", "ulicp", "regulacja"].includes(app.application_type)
            && !(app.wpw_forms_state && app.wpw_forms_state.kinds.length > 0) && (
          <div className="space-y-1 border-t border-[var(--color-border)] pt-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">
                Opis prac ENERGA — wspólny dla działek wniosku{" "}
                <span className="font-normal text-[var(--color-text-muted)]">(opcjonalny — AI bierze dane z mapy)</span>
              </span>
              <Button
                size="sm" variant="secondary"
                loading={m.generateEnergaDescription.isPending}
                disabled={!editable}
                onClick={() => {
                  setEnergaErr("");
                  m.generateEnergaDescription.mutate(app.id, {
                    onSuccess: () => setEnergaDraft(null),
                    onError: (e) => setEnergaErr(apiError(e)),
                  });
                }}
              >
                <Sparkles size={13} className="mr-1" /> Ułóż opis AI
              </Button>
            </div>
            <textarea
              className="input-base !h-auto w-full !py-1"
              rows={3}
              placeholder="np. elektroenergetycznej linii kablowej nn 0,4 kV o długości ok. 120 m oraz dwóch złączy kablowych"
              value={energaDraft ?? app.energa_work_description}
              disabled={!editable}
              onChange={(e) => setEnergaDraft(e.target.value)}
              onBlur={() => {
                if (energaDraft !== null && energaDraft !== app.energa_work_description) {
                  m.update.mutate(
                    { id: app.id, payload: { energa_work_description: energaDraft } },
                    { onSuccess: () => setEnergaDraft(null), onError: (e) => setEnergaErr(apiError(e)) },
                  );
                }
              }}
            />
            <div className="text-[var(--color-text-muted)]">
              Pole OPCJONALNE — nie musisz nic wpisywać: AI układa opis sam z elementów mapy
              na działkach wniosku (także „na żywo" w oknie generowania pisma). Zasila zmienną{" "}
              <span className="font-mono">#dzialki_jst_opis_prac_energa_ai#</span> — frazę do
              zdania „Zakres uzgodnienia obejmuje: X, zgodnie z załącznikiem mapowym”.
              Wpisuj ręcznie tylko, gdy chcesz nadpisać wersję z mapy.
            </div>
            {energaErr && <div className="text-amber-700">⚠ {energaErr}</div>}
          </div>
          )}
        </div>
      </PhaseCard>

      {/* ── FAZA: mapy ── */}
      {mapsPhaseActive && (
        <PhaseCard
          app={app} phase="maps" onApprove={() => approve("maps")} onReopen={() => reopen("maps")}
          error={phaseErrors.maps}
          hint={`System sam znajduje scalone PDF-y formatek pokrywające działki pakietu i SPRAWDZA ŚWIEŻOŚĆ: jeśli od wygenerowania mapy trasa na działce się zmieniła (nowa wersja trasy / flaga „zmiana do weryfikacji”), mapa dostaje etykietę NIEAKTUALNA i blokuje zatwierdzenie fazy — odśwież rozkład formatek albo wgraj aktualny plik ręcznie. Zalecany szablon map dla tego typu: „${config?.formatka_purpose || "—"}”. Liczba egzemplarzy do druku: pole poniżej (starostwa żądają zwykle 2–4).`}
        >
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1">
                Egzemplarze map:
                <input
                  type="number" min={1} max={6}
                  className="input-base !w-16 !py-0.5"
                  value={app.copies}
                  disabled={!editable}
                  onChange={(e) => m.update.mutate({ id: app.id, payload: { copies: Number(e.target.value) } })}
                />
              </label>
              <Button
                size="sm" variant="secondary"
                loading={m.generateFormatki.isPending || formatkiRunning}
                disabled={!editable}
                title="Modal: szablon, skala i treści #zmiennych# tabelki; ramki rozmieszcza automat. Wynik = ZIP do plotowania — wgrane PDF-y arkuszy scalą się i dołączą same."
                onClick={() => { setFormatkiErr(""); setFormatkiModal(true); }}
              >
                <Sparkles size={13} className="mr-1" /> Generuj formatki dla działek pakietu
              </Button>
              <Link
                to={`/power-projects/${app.project_id}?tab=map`}
                className="text-[var(--color-primary)] hover:underline"
                title="Kreator formatek żyje w zakładce Mapa projektu"
              >
                Rozłóż nowe formatki (kreator) →
              </Link>
            </div>
            {formatkiErr && <div className="text-amber-700">⚠ {formatkiErr}</div>}
            {formatkiRunning && (
              <div className="text-[var(--color-text-muted)]">
                Generowanie ZIP-a w tle… pojawi się w „Znalezionych planach" (pobierz → splotuj → wgraj PDF-y).
              </div>
            )}
            {formatkiDone && (
              <div className="text-green-700">
                ✓ ZIP gotowy — pobierz go poniżej, splotuj arkusze do PDF i wgraj; mapa dołączy się sama.
              </div>
            )}
            <div className="section-label">Znalezione plany (formatki)</div>
            {!autoMaps?.length ? (
              <div className="text-[var(--color-text-muted)]">Brak gotowych formatek dla działek pakietu.</div>
            ) : (
              autoMaps.map((row) => (
                <div key={row.sheet_set_id} className="flex flex-wrap items-center gap-2">
                  <MapIcon size={13} />
                  <span className="flex-1 truncate">
                    {row.name} · {row.covered_teryts.join(", ")}
                    <span className="text-[10px] font-mono text-[var(--color-text-muted)]">
                      {" "}· wygenerowano {fmtDateTime(row.created_at)}
                    </span>
                    {/* Działka współwłasna siedzi w zakresie kilku wniosków, więc każdy
                        widzi tu też cudze formatki. Bez podpisu obie pozycje wyglądają
                        tak samo i łatwo podpiąć plan zrobiony pod inny zakres. */}
                    {row.own ? (
                      <Badge color="green">formatka tego wniosku</Badge>
                    ) : row.source_number ? (
                      <Badge color="gray">
                        z wniosku {row.source_number}
                        {row.source_office ? ` — ${row.source_office}` : ""}
                      </Badge>
                    ) : null}
                    {!row.covers_all && <Badge color="yellow">częściowe pokrycie</Badge>}
                    {row.stale && <Badge color="red">NIEAKTUALNA</Badge>}
                    {row.needs_pdf && <Badge color="yellow">bez PDF-a</Badge>}
                  </span>
                  {row.stale && <span className="text-red-600 text-[10px]">{row.stale_reason}</span>}
                  {row.needs_pdf ? (
                    // ZIP wygenerowany, arkusze nie splotowane: pobierz → plot w CAD →
                    // wgraj PDF-y; scalony PDF dołącza się wtedy sam.
                    <>
                      {row.zip_url && (
                        <a href={row.zip_url} download={row.zip_name ?? undefined}
                           className="inline-flex items-center gap-1 font-mono text-[var(--color-primary)] hover:underline">
                          <Download size={12} /> pobierz ZIP
                        </a>
                      )}
                      <label className={clsx(
                        "inline-flex items-center gap-1 px-2 py-0.5 border border-[var(--color-border-strong)] cursor-pointer hover:bg-[var(--color-bg-elevated)]",
                        (!editable || sheetPdfBusy === row.sheet_set_id) && "opacity-50 pointer-events-none",
                      )}>
                        <Upload size={12} />
                        {sheetPdfBusy === row.sheet_set_id ? "scalanie…" : "wgraj PDF-y arkuszy"}
                        <input
                          type="file" className="hidden" accept=".pdf" multiple
                          disabled={!editable}
                          onChange={(e) => {
                            const files = Array.from(e.target.files ?? []);
                            if (files.length) uploadSheetPdfs(row.sheet_set_id, files);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      <Button size="sm" variant="secondary" disabled={!editable || sheetPdfBusy === row.sheet_set_id}
                              onClick={() => renderSheetPdf(row.sheet_set_id)}
                              title="Serwer wyrenderuje arkusze z DXF-a w paczce i scali je w PLAN_SYTUACYJNY.pdf — bez plotowania w CAD-zie. Rysunek wychodzi wektorowy, wersjonowanie jak przy wgranym plocie.">
                        {sheetPdfBusy === row.sheet_set_id ? "scalanie…" : "scal na serwerze"}
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="secondary" disabled={!editable}
                            onClick={() => m.attachMap.mutate({ id: app.id, sheetSetId: row.sheet_set_id })}>
                      Dołącz
                    </Button>
                  )}
                </div>
              ))
            )}
            <div className="section-label">Mapy w pakiecie</div>
            {!app.maps.length ? (
              <div className="text-[var(--color-text-muted)]">Brak — dołącz formatkę albo wgraj plik PDF.</div>
            ) : app.maps.map((mapRow) => (
              <div key={mapRow.id} className="flex items-center gap-2">
                <a href={mapRow.file.url ?? "#"} download className="font-mono hover:underline truncate">
                  {mapRow.label || mapRow.file.original_filename}
                </a>
                <span className="text-[10px] font-mono text-[var(--color-text-muted)]">
                  wgrano {fmtDateTime(mapRow.created_at)}
                </span>
                {mapRow.stale && (
                  <Badge color="red">NIEAKTUALNA</Badge>
                )}
                {mapRow.stale && <span className="text-red-600 text-[10px]">{mapRow.stale_reason}</span>}
                {editable && (
                  <button className="text-red-600 hover:underline"
                          onClick={() => m.deleteMap.mutate({ id: app.id, mapId: mapRow.id })}>
                    usuń
                  </button>
                )}
              </div>
            ))}
            {editable && (
              <FileDrop
                accept=".pdf"
                busy={m.uploadMap.isPending}
                label="Wgraj mapę (PDF)"
                hint="kliknij lub przeciągnij plik tutaj"
                onFiles={(files) => {
                  const f = files[0];
                  if (f) m.uploadMap.mutate({ id: app.id, file: f });
                }}
              />
            )}
          </div>
        </PhaseCard>
      )}

      {/* ── FAZA: załączniki ── */}
      <PhaseCard
        app={app} phase="attachments" onApprove={() => approve("attachments")} onReopen={() => reopen("attachments")}
        error={phaseErrors.attachments}
        hint="Checklista załączników tego typu procedury. Pozycje AUTO kompletują się same: pełnomocnictwa + potwierdzenia opłat z fazy osoby, decyzje z rejestru decyzji (z pilnowaniem klauzuli ostateczności!), wypisy z modułu Wypisów. Potwierdzenie opłaty skarbowej podpiniesz też z opłaty już OPŁACONEJ w /official-fees („Podepnij z opłaconej opłaty…”) albo wgrasz plik ręcznie. Pozycja „ORYGINAŁ decyzji” to fizyczne przypomnienie do koperty."
      >
        <div className="space-y-2 text-xs">
          {app.checklist.map((item) => (
            <div key={item.kind} className="border border-[var(--color-border)] p-2 space-y-1">
              <div className="flex items-center gap-2">
                <span className={clsx("h-2 w-2 rounded-full",
                  item.satisfied ? "bg-green-500" : item.required ? "bg-red-500" : "bg-gray-300")} />
                <b>{item.label}</b>
                {!item.required && <Badge color="gray">opcjonalny</Badge>}
                {item.hint && <InfoHint text={item.hint} />}
                {item.source === "auto" && <Badge color="blue">auto</Badge>}
                {item.source === "physical" && <Badge color="purple">fizycznie</Badge>}
              </div>
              {item.warning && <div className="text-amber-700">⚠ {item.warning}</div>}
              {item.auto_source === "wypis" && editable && (
                <div className="pl-4">
                  <Button size="sm" variant="secondary" onClick={() => setMergeWypisOpen(true)}
                          title="Z PDF-ów wypisów podpiętych do działek wniosku: propozycja stron wg mapowania, edycja zakresów i sklejenie w JEDEN załącznik (bez dubli).">
                    <FileCog size={13} className="mr-1" /> Sklej wypisy działek (wybór stron)
                  </Button>
                </div>
              )}
              {/* Potwierdzenie opłaty skarbowej: zamiast wgrywać plik ręcznie, można
                  podpiąć potwierdzenie przelewu z opłaty już OPŁACONEJ w /official-fees. */}
              {["fee_confirmation", "application_fee_confirmation"].includes(item.auto_source ?? "")
                && editable && (
                <div className="pl-4 space-y-1">
                  <Button
                    size="sm" variant="secondary"
                    onClick={() => setFeePickerKind(feePickerKind === item.kind ? null : item.kind)}
                    title="Lista opłat opłaconych w /official-fees — potwierdzenie przelewu wybranej opłaty wejdzie jako ten załącznik (bez ponownego wgrywania pliku)."
                  >
                    <Wallet size={13} className="mr-1" />
                    {feePickerKind === item.kind ? "Zamknij wybór opłat" : "Podepnij z opłaconej opłaty…"}
                  </Button>
                  {feePickerKind === item.kind && (
                    <div className="border border-[var(--color-border)] p-2 space-y-1">
                      <div className="section-label">
                        Opłacone opłaty (/official-fees) — opłaty tego projektu pierwsze
                      </div>
                      {!paidFees ? (
                        <Spinner size="sm" />
                      ) : paidFees.length === 0 ? (
                        <EmptyState>Brak opłaconych opłat z potwierdzeniem przelewu.</EmptyState>
                      ) : (
                        paidFees.map((fee) => (
                          <div key={fee.id}
                               className="flex items-center gap-2 border-b border-[var(--color-border)] last:border-b-0 py-1">
                            <span className="flex-1 min-w-0">
                              <b>{fee.office_name}</b> · {fee.amount} zł
                              {fee.same_project && <Badge color="green">ten projekt</Badge>}
                              {fee.is_power_of_attorney && <Badge color="blue">za pełnomocnictwo</Badge>}
                              {fee.status === "acknowledged" && <Badge color="green">odbiór potwierdzony</Badge>}
                              <span className="block text-[var(--color-text-muted)] truncate"
                                    title={fee.confirmations.join(", ")}>
                                {fee.transfer_name}
                                {fee.paid_at ? ` · opłacona ${fmtDate(fee.paid_at)}` : ""}
                                {fee.confirmations.length > 1 ? ` · ${fee.confirmations.length} potwierdzenia` : ""}
                              </span>
                            </span>
                            <Button
                              size="sm" variant="secondary"
                              loading={m.attachFeeConfirmation.isPending}
                              onClick={() => {
                                setErr("attachments", "");
                                m.attachFeeConfirmation.mutate(
                                  { id: app.id, kind: item.kind, feeId: fee.id },
                                  {
                                    onSuccess: () => setFeePickerKind(null),
                                    onError: (e) => setErr("attachments", apiError(e)),
                                  },
                                );
                              }}
                            >
                              Podepnij
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
              {item.files.map((f, i) => (
                <div key={i} className="pl-4 text-[var(--color-text-muted)]">{f.name}</div>
              ))}
              {app.attachments.filter((a) => a.kind === item.kind).map((a) => (
                <div key={a.id} className="pl-4 flex items-center gap-2">
                  <a href={a.file.url ?? "#"} download className="font-mono hover:underline"
                     title={a.note || a.file.original_filename}>
                    {a.file.original_filename}
                  </a>
                  {a.source === "fee" && <Badge color="blue">z opłaty</Badge>}
                  {editable && (
                    <button className="text-red-600 hover:underline"
                            onClick={() => m.deleteAttachment.mutate({ id: app.id, attId: a.id })}>
                      usuń
                    </button>
                  )}
                </div>
              ))}
              {editable && item.source !== "physical" && (
                <FileDrop
                  multiple
                  label={item.required ? "Wgraj plik(i)" : "Wgraj plik(i) — opcjonalne"}
                  hint="kliknij lub przeciągnij tutaj"
                  onFiles={(files) => {
                    if (files.length) m.uploadAttachment.mutate({ id: app.id, kind: item.kind, files });
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </PhaseCard>

      {/* ── FAZA: kompilacja ── */}
      <PhaseCard
        app={app} phase="compile" onApprove={() => approve("compile")} onReopen={() => reopen("compile")}
        error={phaseErrors.compile}
        hint={`Jeden plik pakietu w kolejności: ${config?.qr_cover ? "strona tytułowa z QR (link do pakietu + drukowane podsumowanie) → " : ""}pismo → pełnomocnictwa → mapy → załączniki. Pismo w ODT/DOCX konwertuje się automatycznie do PDF — wynik to zawsze JEDEN scalony PDF (awaryjnie, gdy konwersja się nie powiedzie: paczka ZIP z pismem + pakietem PDF). Pliki z podpisem elektronicznym (system wykrywa podpis w każdym PDF-ie pakietu — pełnomocnictwo PAdES, podpisana decyzja, czasem potwierdzenie przelewu) NIE wchodzą do scalenia — podpis przepadłby przy scalaniu, więc jadą jako osobne pliki obok pakietu (wysyłka dokłada je sama). Zwykły wydruk potwierdzenia przelewu scala się normalnie. Podpisane pismo scala się (jest osią pakietu) — podpis wtedy ginie; podpisz gotowy pakiet. Każde cofnięcie wcześniejszej fazy unieważnia kompilat. Gdy po kompilacji trzeba coś w pakiecie dołożyć albo poprawić poza systemem — wgraj gotowy plik: zastąpi kompilat i to on pojedzie dalej. Przy opłacie skarbowej bez potwierdzenia przelewu pakiet da się skompilować tylko świadomie (potwierdzenie w oknie) — taki kompilat nie zawiera opłaty i karta o tym ostrzega; po opłaceniu skompiluj ponownie. Wysyłka przed przelewem też jest możliwa, ale tylko świadomie (wyraźne ostrzeżenie) — potwierdzenie trzeba potem dosłać do urzędu.`}
      >
        <div className="space-y-2 text-xs">
          {kwWarningNotice}
          {/* Ostrzeżenie PRZED kompilacją: pakiet zbudowany teraz nie obejmie opłaty. */}
          {hasUnpaidFees && editable && packageMissingFees.length === 0 && (
            <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                {unpaidFeeSubject} {unpaidFeeNames.length > 1 ? "nie są" : "nie jest"} jeszcze
                {unpaidFeeNames.length > 1 ? " opłacone" : " opłacona"}. Kompilacja teraz to
                kompilacja <b>bez opłaty</b> — pakiet nie będzie zawierał potwierdzenia przelewu
                (pojawi się ono w /official-fees po opłaceniu).
              </span>
            </div>
          )}
          {/* Ostrzeżenie PO kompilacji: bieżący kompilat nie objął opłaty. */}
          {app.compiled_pdf && !compiling && packageMissingFees.length > 0 && (
            <div className="flex items-start gap-2 border border-amber-400 bg-amber-50 text-amber-900 px-3 py-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>
                <b>Skompilowany wniosek nie zawiera opłaty skarbowej</b> {packageMissingFeesLabel} —
                pakiet skompilowano przed potwierdzeniem przelewu.{" "}
                {packageMissingFeesPaid
                  ? "Przelew jest już potwierdzony: wysyłka dołoży potwierdzenie osobnym plikiem, "
                    + "ale spis treści kompilatu go nie wymienia — skompiluj pakiet ponownie."
                  : "Po potwierdzeniu przelewu skompiluj pakiet ponownie."}
              </span>
            </div>
          )}
          <Button
            size="sm"
            loading={compiling || m.compile.isPending}
            disabled={!editable}
            onClick={() => withKwWarningConfirm("skompiluj pakiet", () => {
              if (hasUnpaidFees) setCompileWithoutFeeConfirm(true);
              else runCompile();
            })}
          >
            <Package size={13} className="mr-1" />
            {app.compiled_pdf ? "Kompiluj ponownie" : "Skompiluj pakiet"}
          </Button>
          {compiling && (
            <div className="flex items-center gap-2"><Spinner size="sm" /> Kompiluję…</div>
          )}
          {app.compiled_pdf && !compiling && (
            <div>
              <a href={app.compiled_pdf.url ?? "#"} download
                 className="inline-flex items-center gap-1 font-mono border border-[var(--color-border-strong)] px-2 py-1 hover:bg-[var(--color-bg-elevated)]">
                <Download size={12} /> {app.compiled_pdf.original_filename}
              </a>
              {app.compiled_is_manual && (config?.signed_package
                ? <Badge color="green" className="ml-2">podpisany pakiet</Badge>
                : <Badge color="yellow" className="ml-2">wgrany ręcznie</Badge>)}
              <span className="ml-2 text-[var(--color-text-muted)]">
                {app.compiled_is_manual
                  ? `wgrany ${fmtDateTime(app.compiled_at)} — obejrzyj przed wysyłką`
                  : `skompilowano ${fmtDateTime(app.compiled_at)} — obejrzyj przed wysyłką`}
              </span>
              {(app.separate_files?.length ?? 0) > 0 && (
                <div className="mt-2">
                  <Button
                    size="sm"
                    loading={zipBusy}
                    title="Scalony PDF + pełnomocnictwa i potwierdzenia opłat w oryginalnych bajtach — to jest wydawany pakiet"
                    onClick={async () => {
                      setZipBusy(true);
                      setErr("compile", "");
                      try {
                        await downloadApplicationPackageZip(app.id, app.number);
                      } catch (e) {
                        setErr("compile", apiError(e));
                      } finally {
                        setZipBusy(false);
                      }
                    }}
                  >
                    <Package size={13} className="mr-1" /> Pobierz pakiet (ZIP: PDF + osobne pliki)
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Pliki jadące OBOK pakietu: KAŻDE pełnomocnictwo (oryginalne bajty — podpis
              elektroniczny musi przeżyć; scalenie przez pypdf go unieważnia), potwierdzenia
              opłaty skarbowej i inne pliki z wykrytym podpisem. Wysyłka dokłada je do sprawy
              / gk_docs sama; „Pobierz pakiet (ZIP)" pakuje je obok kompilatu. */}
          {(app.separate_files?.length ?? 0) > 0 && (
            <div className="border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 space-y-1">
              <div className="font-medium">Osobne pliki pakietu (nie scalamy ich z pismem)</div>
              <div className="text-[var(--color-text-muted)]">
                Pełnomocnictwo i potwierdzenia opłaty jadą w oryginalnych bajtach — każda
                ingerencja w plik unieważnia podpis elektroniczny. Wysyłka dołoży je do sprawy
                automatycznie; przy wysyłce papierowej wydrukuj/dołącz je osobno.
              </div>
              <div className="flex flex-wrap gap-2">
                {app.separate_files?.map((row) => (
                  <a key={row.file.id} href={row.file.url ?? "#"} download
                     title={row.reason || row.label}
                     className="inline-flex items-center gap-1 font-mono border border-[var(--color-border-strong)] px-2 py-1 hover:bg-[var(--color-bg-base)]">
                    <Download size={12} /> {row.file.original_filename}
                    {row.role && (
                      <span className="ml-1 text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                        {SEPARATE_FILE_ROLE_LABELS[row.role]}
                      </span>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Podpis na GOTOWYM pakiecie (ULICP): podpis wgrany wcześniej na samym druku
              ginął przy scalaniu, więc podpisuje się kompilat i wgrywa go z powrotem —
              bez tego backend nie zamknie fazy i nie wypuści wniosku. */}
          {config?.signed_package && app.compiled_pdf && !compiling && (
            app.compiled_is_manual ? (
              <div className="flex items-start gap-2 border border-green-300 bg-green-50 text-green-800 px-3 py-2">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                <span>
                  Podpisany pakiet wgrany — to on pojedzie do urzędu. Zmiana pisma, map czy
                  załączników wymaga ponownej kompilacji i <b>ponownego podpisu</b>
                  („Kompiluj ponownie" zdejmuje podpisany pakiet).
                </span>
              </div>
            ) : (
              <div className="flex items-start gap-2 border border-amber-400 bg-amber-50 text-amber-900 px-3 py-2">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <b>Ostatni krok: podpisz pakiet.</b> Do urzędu idzie podpisany pakiet, nie sam kompilat.
                  <ol className="list-decimal pl-5 mt-1 space-y-0.5">
                    <li>Pobierz skompilowany pakiet (link powyżej).</li>
                    <li>Uzupełnij pola druku wypełniane ręcznie i podpisz pakiet.</li>
                    <li>Wgraj podpisany pakiet w polu „Wgraj podpisany pakiet" poniżej.</li>
                  </ol>
                </div>
              </div>
            )
          )}

          {/* Ręczna korekta pakietu: po kompilacji zdarza się dołożyć stronę albo poprawić
              coś w PDF poza systemem. Wgrany plik ZASTĘPUJE kompilat (a powrót do generatu
              to po prostu „Kompiluj ponownie"), więc faza wraca do zatwierdzenia. */}
          <OrDivider />
          <FileDrop
            accept=".pdf,.odt,.docx,.doc,.rtf"
            disabled={!editable}
            busy={m.uploadCompiled.isPending}
            label={config?.signed_package
              ? "Wgraj podpisany pakiet — przeciągnij tutaj lub kliknij, aby wybrać"
              : "Wgraj poprawiony pakiet — przeciągnij tutaj lub kliknij, aby wybrać"}
            hint={config?.signed_package
              ? "Podpisany pakiet (PDF; ODT/DOCX/DOC/RTF przekonwertujemy) — zastąpi kompilat i to on pojedzie do urzędu."
              : "Gotowy plik pakietu (PDF; ODT/DOCX/DOC/RTF przekonwertujemy) — zastąpi kompilat i to on pojedzie dalej."}
            onFiles={(files) => {
              const f = files[0];
              if (!f) return;
              setErr("compile", "");
              m.uploadCompiled.mutate({ id: app.id, file: f }, {
                onError: (e) => setErr("compile", apiError(e)),
              });
            }}
          />
          {app.compiled_is_manual && !config?.signed_package && (
            <div className="text-amber-700 dark:text-amber-400">
              ⚠ Pakiet pochodzi z ręcznego wgrania, nie ze złożenia dokumentów z systemu.
              Zmiany w piśmie, mapach czy załącznikach NIE trafią do niego same — użyj
              „Kompiluj ponownie", jeśli pakiet ma znów powstać z systemu.
            </div>
          )}
        </div>
      </PhaseCard>

      {/* ── FAZA: wysyłka ── */}
      <PhaseCard
        app={app} phase="submit" onApprove={() => approve("submit")} onReopen={() => reopen("submit")}
        error={phaseErrors.submit}
        hint={
          config?.recipient_mode === "negotiator"
            ? "Przekazanie pakietu do teczek negocjatora: pakiet trafi jako dokumenty wejściowe GK na przekazania WPW właścicieli tej działki (u wykonawcy zapali się „Zaktualizowana”)."
            : `Wysyłka zakłada sprawę w Korespondencji projektu z pismem w obiegu szkic → akceptacja → wysłano (wymaga uprawnień zatwierdzania/składania w projekcie — panel Metadane). Kanał papierowy automatycznie trafia do dziennika DLC_02. ${
                edeliveryEnabled
                  ? "Kanał e-Doręczeń wstawia pismo do kolejki DLC_09 — wyjdzie dopiero po akceptacji i kliknięciu „Wyślij” przez CEO (jeśli urząd nie ma adresu e-Doręczeń albo nie ma skrzynki, pismo zostaje samym odnotowaniem)."
                  : "ePUAP/e-doręczenia odnotowujesz ręcznie z numerem (bez integracji)."
              } Od wysłania liczy się termin KPA.`
        }
      >
        <div className="space-y-2 text-xs">
          {/* Sprawa, w której wyląduje pismo: nowa (domyślnie) albo istniejąca sprawa projektu,
              opcjonalnie jej pozycja „wniosek" bez pakietu. Zmienialne do wysyłki. */}
          {config?.recipient_mode !== "negotiator" && editable && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2">
              <span className="section-label">Sprawa w Korespondencji</span>
              {app.proceeding_info ? (
                <span>
                  podpięta: <b>{app.proceeding_info.type_name}</b>
                  {app.proceeding_info.status === "closed" && (
                    <span className="text-amber-700 dark:text-amber-400">
                      {" "}(zamknięta — wysyłka zablokowana; „Zmień” → wybierz ją i potwierdź ponowne otwarcie albo „Odepnij”)
                    </span>
                  )}
                  {app.proceeding_item_info ? (
                    <> · do pozycji „Wniosek”{app.proceeding_item_info.our_ref ? ` znak ${app.proceeding_item_info.our_ref}` : ""}
                      {app.proceeding_item_info.date_sent ? ` (wysłano ${fmtDate(app.proceeding_item_info.date_sent)})` : ""}</>
                  ) : (
                    <> · wysyłka doda nową pozycję „Wniosek”</>
                  )}
                  {" "}·{" "}
                  <Link className="text-[var(--color-primary)] hover:underline"
                        to={`/power-projects/${app.project_id}?tab=correspondence&proceeding=${app.proceeding_info.id}`}>
                    otwórz →
                  </Link>
                </span>
              ) : (
                <span>wysyłka założy nową sprawę</span>
              )}
              <span className="ml-auto flex gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => {
                  setCaseDraft(app.proceeding ? {
                    proceeding: app.proceeding, item: app.proceeding_item,
                    closed: app.proceeding_info?.status === "closed", reopen: false,
                  } : null);
                  setCaseLinkOpen(true);
                }}>
                  {app.proceeding_info ? "Zmień" : "Podepnij do istniejącej sprawy"}
                </Button>
                {app.proceeding_info && (
                  <Button size="sm" variant="ghost" loading={m.linkProceeding.isPending}
                    onClick={() => {
                      setErr("submit", "");
                      m.linkProceeding.mutate({ id: app.id, proceeding: null }, {
                        onError: (e) => setErr("submit", apiError(e)),
                      });
                    }}>
                    Odepnij
                  </Button>
                )}
              </span>
            </div>
          )}
          {/* Klauzula: ostatnie przypomnienie przed wysyłką — bez ORYGINAŁU w kopercie
              organ nie ma na czym postawić klauzuli. Wysyłka odnotuje, że wyszedł. */}
          {app.application_type === "klauzula" && editable && (
            <div className="border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
              Do koperty włóż <b>ORYGINAŁ decyzji{app.decision_detail ? ` ${app.decision_detail.sign}` : ""}</b>{" "}
              — organ stawia klauzulę na nim. Wysyłka odnotuje, że oryginał wyszedł z firmy, a decyzja
              w rejestrze dostanie stan „wniosek o klauzulę w toku”.
            </div>
          )}
          {/* Sam koniec WNU (pakiet dla negocjatora): gdy zostaje już tylko przekazanie
              podwykonawcy, przypominamy na czerwono, że zapotrzebowanie z tego zlecenia
              musi być sprawdzone i zaakceptowane — inaczej podwykonawca nic nie dostanie. */}
          {config?.recipient_mode === "negotiator"
            && app.status !== "submitted" && app.status !== "completed"
            && app.phases.every((p) => p.key === "submit" || p.state === "approved") && (
              <RequisitionApprovalNotice withLink />
          )}
          {app.status === "cancelled" && !app.submitted_at ? (
            <p className="text-[var(--color-text-muted)]">
              Wniosek anulowany — nie można go wysłać. Poprawkę składasz nowym wnioskiem.
            </p>
          ) : app.status === "submitted" || app.status === "completed" || app.status === "cancelled" ? (
            <div className="space-y-1">
              {app.status === "submitted" && submittedMissingFees.length > 0 && (
                <div className="flex items-start gap-2 border border-red-300 bg-red-50 text-red-800 px-3 py-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    <b>Wniosek wysłano BEZ opłaty skarbowej</b>{" "}
                    {submittedMissingFees.map((f) => f.label).join(" i ")}.{" "}
                    {submittedMissingFees.every((f) => f.paid)
                      ? "Przelew jest już potwierdzony — dośl potwierdzenie do urzędu (plik w /official-fees); system nie zrobi tego sam."
                      : "Po opłaceniu dośl potwierdzenie przelewu do urzędu — system nie zrobi tego sam."}
                  </span>
                </div>
              )}
              {/* Wycofany po złożeniu: wniosek zakończony jako „Anulowany” — zostaje historia
                  złożenia i wycofanego pisma (DLC_02 „Anulowane” / DLC_09 „Wycofana”). */}
              {app.status === "cancelled" && (
                <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2">
                  Wycofany {fmtDateTime(app.withdrawn_at)}
                  {app.withdrawn_by_name ? ` (${app.withdrawn_by_name})` : ""} — wniosek anulowany,
                  pismo nie zostało wysłane. Poprawkę składasz nowym wnioskiem.
                </div>
              )}
              <div>
                Złożony {fmtDateTime(app.submitted_at)} ({app.submitted_by_name})
                {/* Obieg akceptacji: złożył przekazujący, dyrektor tylko zaakceptował. */}
                {app.submission_approved_by_name && (
                  <> · zaakceptował(a) {app.submission_approved_by_name}
                    {app.submission_approved_at ? ` ${fmtDateTime(app.submission_approved_at)}` : ""}</>
                )}
              </div>
              {/* Papier: po złożeniu widać tu, co dzieje się z przesyłką w dzienniku DLC_02
                  (kolejka / wysłane / doręczone) — bez tego jedynym śladem była etykieta kanału. */}
              <PaperDispatchLine dispatch={app.dispatch_info} />
              <EdeliveryLine edelivery={app.edelivery_info} />
              {/* Wycofanie złożonego wniosku: tylko gdy pismo jeszcze nie wyszło (papier
                  w kolejce DLC_02, e-Doręczenie przed akceptacją). Inaczej — dlaczego nie. */}
              {app.status === "submitted" && app.can_manage && app.withdrawal && (
                app.withdrawal.blocker ? (
                  <p className="text-[11px] text-[var(--color-text-muted)]">
                    Wycofanie niemożliwe: {app.withdrawal.blocker}
                  </p>
                ) : (
                  <div>
                    <Button size="sm" variant="ghost" className="text-red-600"
                      onClick={() => { m.withdrawSubmission.reset(); setWithdrawOpen(true); }}>
                      <Undo2 size={13} className="mr-1" /> Wycofaj złożony wniosek
                    </Button>
                  </div>
                )
              )}
              {withdrawOpen && app.withdrawal && (
                <WithdrawSubmissionModal
                  number={app.number}
                  channel={app.withdrawal.channel}
                  caseEffect={app.withdrawal.case_effect}
                  untracked={app.withdrawal.untracked}
                  pending={m.withdrawSubmission.isPending}
                  error={m.withdrawSubmission.error ? apiError(m.withdrawSubmission.error) : null}
                  onClose={() => setWithdrawOpen(false)}
                  onConfirm={() =>
                    m.withdrawSubmission.mutate(
                      { id: app.id, confirmUntracked: !!app.withdrawal?.untracked },
                      { onSuccess: () => setWithdrawOpen(false) },
                    )
                  }
                />
              )}
              {app.proceeding_info && (
                <div>
                  Sprawa „{app.proceeding_info.type_name}”:{" "}
                  {app.proceeding_info.status === "closed" ? (
                    <>
                      zamknięta
                      {app.proceeding_info.closed_at ? ` ${fmtDateTime(app.proceeding_info.closed_at)}` : ""}
                      {" "}({ITEM_OUTCOME_OPTIONS.find((o) => o.value === app.proceeding_info?.outcome)?.label ?? app.proceeding_info.outcome})
                    </>
                  ) : (
                    <>otwarta (KPA {app.proceeding_info.kpa_days} dni)</>
                  )}{" "}
                  · <Link className="text-[var(--color-primary)] hover:underline"
                          to={`/power-projects/${app.project_id}?tab=correspondence&proceeding=${app.proceeding_info.id}`}>
                    otwórz w Korespondencji →
                  </Link>
                  {app.proceeding_info.auto && (
                    <span className="text-[var(--color-text-muted)]"> · założona automatycznie przy wysyłce</span>
                  )}
                  {app.proceeding_info.auto && app.proceeding_info.can_detach_auto && (
                    <Button size="sm" variant="ghost" className="ml-1 text-red-600"
                      title="Odepnij wniosek — sprawa założona automatycznie zostanie usunięta"
                      onClick={() => { m.linkProceeding.reset(); setDetachAutoOpen(true); }}>
                      Odepnij (usuwa sprawę)
                    </Button>
                  )}
                </div>
              )}
              {detachAutoOpen && app.proceeding_info && (
                <DetachAutoCaseModal
                  number={app.number}
                  caseName={app.proceeding_info.type_name}
                  pending={m.linkProceeding.isPending}
                  error={m.linkProceeding.error ? apiError(m.linkProceeding.error) : null}
                  onClose={() => setDetachAutoOpen(false)}
                  onConfirm={() =>
                    m.linkProceeding.mutate(
                      { id: app.id, proceeding: null, delete_case: true },
                      { onSuccess: () => setDetachAutoOpen(false) },
                    )
                  }
                />
              )}
              {/* Złożony bez sprawy (wysłany przed 2026-09-17, gdy wysyłka mogła jej nie
                  zakładać) — da się go jeszcze podpiąć pod sprawę projektu. */}
              {app.status === "submitted" && !app.proceeding_info && config?.recipient_mode !== "negotiator" && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[var(--color-text-muted)]">Wniosek złożony bez sprawy w Korespondencji.</span>
                  <Button size="sm" variant="ghost" onClick={() => { setCaseDraft(null); setCaseLinkOpen(true); }}>
                    Podepnij do istniejącej sprawy
                  </Button>
                </div>
              )}
            </div>
          ) : app.status === "pending_approval" ? (
            /* Kolejka „do akceptacji dyrektora": wniosek zamrożony. Dyrektor (albo osoba
               z can_approve) zatwierdza — to wypuszcza pismo kanałem z przekazania —
               albo zwraca do poprawy z notatką. Przekazujący może wycofać.
               Obieg wyłączony (APPLICATION_APPROVAL_ENABLED): tu trafiają już tylko wnioski
               przekazane przed wyłączeniem — każdy je wyśle, a wycofa do szkicu tylko
               przekazujący albo autor (can_withdraw_approval). */
            <div className="space-y-2">
              <div className="border border-purple-300 bg-purple-50 text-purple-900 px-3 py-2">
                Przekazany do akceptacji {fmtDateTime(app.approval_requested_at)}
                {app.approval_requested_by_name ? ` (${app.approval_requested_by_name})` : ""}
                {" · kanał: "}
                {({ paper: "Papier (dziennik DLC_02)", edoreczenia: "e-Doręczenia",
                    mail: "E-mail", other: "Inny" } as Record<string, string>)[app.approval_channel]
                  ?? app.approval_channel}
                {app.approval_channel_ref ? ` (${app.approval_channel_ref})` : ""}
              </div>
              {app.can_approve_submission ? (
                <div className="flex flex-wrap items-end gap-3">
                  <Button
                    loading={m.submit.isPending}
                    onClick={() => {
                      setErr("submit", "");
                      // Bez body — backend użyje kanału zapamiętanego z przekazania.
                      m.submit.mutate(
                        { id: app.id, channel: app.approval_channel, channelRef: app.approval_channel_ref },
                        { onError: (e) => setErr("submit", apiError(e)) },
                      );
                    }}
                  >
                    <Send size={13} className="mr-1" /> Zatwierdź i przekaż do wysłania
                  </Button>
                  {APPLICATION_APPROVAL_ENABLED ? (
                    <Button variant="secondary" onClick={() => setRejectOpen(true)}>
                      <Undo2 size={13} className="mr-1" /> Zwróć do poprawy
                    </Button>
                  ) : app.can_withdraw_approval && withdrawButton}
                </div>
              ) : app.can_withdraw_approval ? withdrawButton : (
                <p className="text-[11px] text-[var(--color-text-muted)]">
                  Wycofać wniosek z akceptacji może tylko osoba, która go przekazała, albo jego autor.
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              {/* Opłata bez potwierdzenia przelewu: wysyłka możliwa, ale wniosek pójdzie do
                  urzędu bez dowodu zapłaty — wyraźne ostrzeżenie + potwierdzenie w oknie. */}
              {hasUnpaidFees && (
                <div className="basis-full flex items-start gap-2 border border-red-300 bg-red-50 text-red-800 px-3 py-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    <b>UWAGA: {unpaidFeeSubject} {unpaidFeeNames.length > 1 ? "nie są" : "nie jest"} jeszcze
                    {unpaidFeeNames.length > 1 ? " opłacone" : " opłacona"}.</b> Wysłanie teraz to wysłanie
                    wniosku <b>BEZ potwierdzenia opłaty skarbowej</b> — urząd może wezwać do uzupełnienia,
                    a nieuzupełniony wniosek pozostawić bez rozpatrzenia. Potwierdzenie przelewu nie
                    dojdzie samo: po opłaceniu trzeba je dosłać osobno.
                  </span>
                </div>
              )}
              {packageMissingFees.length > 0 && !hasUnpaidFees && (
                <div className="basis-full flex items-start gap-2 border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    Kompilat powstał przed potwierdzeniem przelewu ({packageMissingFeesLabel}) —
                    potwierdzenie dołączy się do wysyłki osobnym plikiem, ale spis treści pakietu go
                    nie wymienia. Rozważ ponowną kompilację przed wysyłką.
                  </span>
                </div>
              )}
              {config?.recipient_mode !== "negotiator" && (
                <>
                  <label className="block">
                    <span className="section-label">Kanał</span>
                    <select className="input-base mt-1" value={submitChannel}
                            onChange={(e) => setSubmitChannel(e.target.value)}>
                      <option value="paper">Papier (dziennik DLC_02)</option>
                      {/* Kanał e-Doręczeń jest zawsze — zmienia się tylko to, co się za nim
                          dzieje. Moduł DLC_09 włączony: pismo idzie do kolejki wysyłki.
                          Wyłączony globalnie: po staremu, czyli ręczne odnotowanie z numerem. */}
                      <option value="edoreczenia">
                        {edeliveryEnabled
                          ? "e-Doręczenia (kolejka DLC_09)"
                          : "e-Doręczenia / ePUAP (odnotowanie)"}
                      </option>
                      <option value="mail">E-mail</option>
                      <option value="other">Inny</option>
                    </select>
                  </label>
                  {/* Numer nadania wpisuje człowiek tam, gdzie go zna: mail, inny kanał oraz
                      e-Doręczenia przy WYŁĄCZONEJ integracji (ręczne odnotowanie, jak dotąd).
                      Przy włączonym DLC_09 identyfikator nadaje operator dopiero przy wysyłce
                      i wraca na pismo sam — nie ma czego wpisywać przy składaniu. */}
                  {(submitChannel === "mail"
                    || submitChannel === "other"
                    || (submitChannel === "edoreczenia" && !edeliveryEnabled)) && (
                    <label className="block">
                      <span className="section-label">Numer / identyfikator nadania</span>
                      <input className="input-base mt-1" value={submitRef}
                             onChange={(e) => setSubmitRef(e.target.value)} />
                    </label>
                  )}
                  {edeliveryEnabled && submitChannel === "edoreczenia" && (
                    <div className="space-y-1">
                      <EdeliveryRecipientHint office={app.recipient_office_detail} />
                      <p className="max-w-lg text-[11px] text-[var(--color-text-muted)]">
                        Numer nadania nada operator przy wysyłce — wróci na pismo sam.
                      </p>
                    </div>
                  )}
                </>
              )}
              {config?.recipient_mode === "negotiator" || app.can_approve_submission ? (
                <Button
                  loading={m.submit.isPending}
                  onClick={() => {
                    if (hasUnpaidFees) setSubmitWithoutFeeConfirm(true);
                    else runSubmit();
                  }}
                >
                  <Send size={13} className="mr-1" />
                  {config?.recipient_mode === "negotiator" ? "Przekaż negocjatorowi" : "Zatwierdź i przekaż do wysłania"}
                </Button>
              ) : (
                /* Koordynator nie wypuszcza pisma: kompletuje wniosek i przekazuje go
                   do weryfikacji — pismo wychodzi dopiero po akceptacji dyrektora. */
                <Button
                  loading={m.requestApproval.isPending}
                  onClick={() => {
                    setErr("submit", "");
                    m.requestApproval.mutate(
                      { id: app.id, channel: submitChannel, channelRef: submitRef },
                      { onError: (e) => setErr("submit", apiError(e)) },
                    );
                  }}
                >
                  <Send size={13} className="mr-1" /> Przekaż do akceptacji dyrektora
                </Button>
              )}
            </div>
          )}
          {APPLICATION_APPROVAL_ENABLED && app.approval_review_note
            && !["submitted", "completed", "cancelled"].includes(app.status) && (
            <div className="border border-red-300 bg-red-50 text-red-800 px-3 py-2 mt-2">
              Zwrócony do poprawy: {app.approval_review_note}
            </div>
          )}
        </div>
      </PhaseCard>

      {/* modal opłaty 17 zł */}
      {feeModalLink && (
        <PoaFeeModal
          app={app}
          linkId={feeModalLink.linkId}
          initialMode={feeModalLink.mode}
          candidates={feeCandidates ?? []}
          onClose={() => setFeeModalLink(null)}
          onSubmit={(payload) => {
            m.setPoaFee.mutate(
              { id: app.id, payload: { link: feeModalLink.linkId, ...payload } },
              { onSuccess: () => setFeeModalLink(null), onError: (e) => setErr("poa", apiError(e)) },
            );
          }}
          onSetOffice={(office) =>
            m.setStampDutyOffice.mutate(
              { id: app.id, office },
              { onError: (e) => setErr("poa", apiError(e)) },
            )
          }
          pending={m.setPoaFee.isPending || m.setStampDutyOffice.isPending}
        />
      )}

      {/* modal opłaty od wniosku (ULICP 598 zł) */}
      {appFeeModal && app.application_fee_info && (
        <ApplicationFeeModal
          app={app}
          info={app.application_fee_info}
          initialMode={appFeeModal.mode}
          candidates={appFeeCandidates ?? []}
          onClose={() => setAppFeeModal(null)}
          onSubmit={(payload) => {
            m.setApplicationFee.mutate(
              { id: app.id, payload },
              { onSuccess: () => setAppFeeModal(null), onError: (e) => setErr("poa", apiError(e)) },
            );
          }}
          onSetOffice={(office) =>
            m.setApplicationFeeOffice.mutate(
              { id: app.id, office },
              { onError: (e) => setErr("poa", apiError(e)) },
            )
          }
          pending={m.setApplicationFee.isPending || m.setApplicationFeeOffice.isPending}
        />
      )}

      {/* potwierdzenie: wysyłka wniosku przed przelewem (wniosek bez opłaty) */}
      <Modal
        open={submitWithoutFeeConfirm}
        onClose={() => setSubmitWithoutFeeConfirm(false)}
        title="Wysłać wniosek bez opłaty?"
        size="sm"
      >
        <div className="space-y-4 text-sm">
          <div className="flex gap-3">
            <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={20} />
            <p className="text-[var(--color-text-primary)]">
              <b>{unpaidFeeSubject}</b>{" "}
              {unpaidFeeNames.length > 1 ? "nie są jeszcze opłacone" : "nie jest jeszcze opłacona"}.
              Wniosek pójdzie do urzędu <b>BEZ potwierdzenia opłaty skarbowej</b>.
            </p>
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">
            Urząd może wezwać do uzupełnienia, a nieuzupełniony wniosek pozostawić bez
            rozpatrzenia. Potwierdzenie przelewu NIE dojdzie do urzędu samo — po opłaceniu
            trzeba je dosłać osobno (karta wysyłki będzie o tym przypominać).
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setSubmitWithoutFeeConfirm(false)}>
              Anuluj
            </Button>
            <Button variant="danger" size="sm" loading={m.submit.isPending} onClick={() => runSubmit(true)}>
              Tak, wyślij bez opłaty
            </Button>
          </div>
        </div>
      </Modal>

      {/* potwierdzenie: kompilacja pakietu przed przelewem (kompilat bez opłaty) */}
      <Modal
        open={compileWithoutFeeConfirm}
        onClose={() => setCompileWithoutFeeConfirm(false)}
        title="Skompilować pakiet bez opłaty?"
        size="sm"
      >
        <div className="space-y-4 text-sm">
          <div className="flex gap-3">
            <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
            <p className="text-[var(--color-text-primary)]">
              <b>{unpaidFeeSubject}</b>{" "}
              {unpaidFeeNames.length > 1 ? "nie są jeszcze opłacone" : "nie jest jeszcze opłacona"}.
              Skompilowany pakiet <b>nie będzie zawierał potwierdzenia przelewu</b>.
            </p>
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">
            Po opłaceniu skompiluj pakiet ponownie — karta kompilacji będzie o tym przypominać.
            Wysłanie wniosku przed przelewem system potraktuje jako wysyłkę bez opłaty (osobne
            ostrzeżenie).
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setCompileWithoutFeeConfirm(false)}>
              Anuluj
            </Button>
            <Button size="sm" loading={m.compile.isPending} onClick={() => runCompile(true)}>
              Tak, kompiluj bez opłaty
            </Button>
          </div>
        </div>
      </Modal>

      {/* potwierdzenie: zatwierdzenie fazy „poa” bez potwierdzenia przelewu opłaty */}
      <Modal
        open={unpaidFeeApproveConfirm}
        onClose={() => setUnpaidFeeApproveConfirm(false)}
        title="Zatwierdzić fazę bez potwierdzenia opłaty?"
        size="sm"
      >
        <div className="space-y-4 text-sm">
          <div className="flex gap-3">
            <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
            <p className="text-[var(--color-text-primary)]">
              {unpaidFeeNames.length > 1 ? "Opłaty skarbowe" : "Opłata skarbowa"}{" "}
              <b>{unpaidFeeNames.join(" i ") || "tego wniosku"}</b>{" "}
              {unpaidFeeNames.length > 1 ? "są zlecone, ale nie mają" : "jest zlecona, ale nie ma"}{" "}
              jeszcze potwierdzenia przelewu. Zatwierdzasz fazę „Osoba i pełnomocnictwa” mimo to,
              żeby przygotować dalsze fazy (dokument, mapy, załączniki).
            </p>
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">
            Gdy w /official-fees pojawi się potwierdzenie przelewu, pojedzie ono z pakietem samo.
            Kompilacja i wysyłka przed przelewem są możliwe, ale bez opłaty — system zapyta
            i ostrzeże przy każdej z nich. Jeśli opłata zostanie odrzucona albo wycofana, ta faza
            wróci do edycji.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setUnpaidFeeApproveConfirm(false)}>
              Anuluj
            </Button>
            <Button
              size="sm"
              loading={m.approvePhase.isPending}
              onClick={() => {
                setErr("poa", "");
                m.approvePhase.mutate(
                  { id: app.id, phase: "poa", allowUnpaidFees: true },
                  {
                    onSuccess: () => setUnpaidFeeApproveConfirm(false),
                    onError: (e) => {
                      setUnpaidFeeApproveConfirm(false);
                      setErr("poa", apiError(e));
                    },
                  },
                );
              }}
            >
              Tak, zatwierdź bez potwierdzenia
            </Button>
          </div>
        </div>
      </Modal>

      {/* potwierdzenie: opłaty skarbowe rozliczone poza systemem */}
      <Modal
        open={settleFeesConfirm}
        onClose={() => setSettleFeesConfirm(false)}
        title="Opłaty skarbowe poza systemem"
        size="sm"
      >
        <div className="space-y-4 text-sm">
          <div className="flex gap-3">
            <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
            <p className="text-[var(--color-text-primary)]">
              Potwierdzasz, że opłaty skarbowe za pełnomocnictwa tego wniosku zostały rozliczone{" "}
              <b>poza systemem</b> (wniosek wprowadzany wstecznie albo opłata wniesiona przy okienku).
            </p>
          </div>
          <p className="text-xs text-[var(--color-text-muted)]">
            Zdejmie to wymóg opłaty 17 zł przy każdym pełnomocnictwie w tej fazie. Pełnomocnictwo
            nadal musi być podpięte i ważne. Potwierdzenie opłaty wgraj w fazie „Załączniki".
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setSettleFeesConfirm(false)}>
              Anuluj
            </Button>
            <Button
              size="sm"
              loading={m.update.isPending}
              onClick={() => {
                m.update.mutate(
                  { id: app.id, payload: { poa_fees_settled_externally: true } },
                  { onSuccess: () => setSettleFeesConfirm(false) },
                );
              }}
            >
              Tak, rozliczone poza systemem
            </Button>
          </div>
        </div>
      </Modal>

      {docModal && (
        <GenerateApplicationDocModal app={app} onClose={() => setDocModal(false)} />
      )}
      {editorDoc && (
        <OdtEditorScreen
          kind={editorDoc.kind}
          id={editorDoc.id}
          title={`Edycja pisma — ${editorDoc.label} (${app.number})`}
          onClose={() => {
            setEditorDoc(null);
            // Zapis w edytorze = nowa wersja generatu / nowy własny plik → świeży detal
            // (document_fill, stan fazy) i historia generatów.
            qc.invalidateQueries({ queryKey: ["pp", "application", app.id] });
            qc.invalidateQueries({ queryKey: ["pp", "generated-docs"] });
          }}
        />
      )}
      {signedDocPrompt && (
        <Modal
          open
          onClose={() => setSignedDocPrompt(false)}
          title="Najpierw wgraj podpisany wniosek"
          size="md"
        >
          <div className="space-y-3 text-sm">
            <p>
              Do urzędu idzie <b>podpisany druk</b>, a nie sam plik z generatora. Formularz ma
              pola uzupełniane ręką (m.in. opis inwestycji) i miejsce na podpis, więc tej fazy
              nie da się zamknąć samym generatem.
            </p>
            <ol className="list-decimal pl-5 space-y-1 text-[var(--color-text-secondary)]">
              <li>Wygeneruj wniosek z szablonu („Generuj z szablonu").</li>
              <li>Wydrukuj, uzupełnij pola wypełniane ręcznie i podpisz.</li>
              <li>
                Zeskanuj i wgraj w polu <b>„Wgraj plik pdf"</b> — przeciągnij plik lub kliknij,
                aby wybrać (PDF/ODT/DOCX).
              </li>
            </ol>
            <p className="text-[var(--color-text-muted)]">
              Wgrany plik zastąpi generat w kompilacji pakietu — to on pojedzie do urzędu.
            </p>
            <div className="flex justify-end">
              <Button onClick={() => setSignedDocPrompt(false)}>Rozumiem</Button>
            </div>
          </div>
        </Modal>
      )}
      {signedPackagePrompt && (
        <Modal
          open
          onClose={() => setSignedPackagePrompt(false)}
          title="Najpierw wgraj podpisany pakiet"
          size="md"
        >
          <div className="space-y-3 text-sm">
            <p>
              Do urzędu idzie <b>podpisany pakiet</b>, a nie sam kompilat z systemu. Podpis
              składa się na gotowym pakiecie — podpis złożony wcześniej na samym druku
              przepadłby przy scalaniu.
            </p>
            <ol className="list-decimal pl-5 space-y-1 text-[var(--color-text-secondary)]">
              <li>Pobierz skompilowany pakiet.</li>
              <li>Uzupełnij pola druku wypełniane ręcznie i podpisz pakiet.</li>
              <li>
                Wgraj go w polu <b>„Wgraj podpisany pakiet"</b> — przeciągnij plik lub kliknij,
                aby wybrać.
              </li>
            </ol>
            <p className="text-[var(--color-text-muted)]">
              Wgrany pakiet zastąpi kompilat — to on pojedzie do urzędu. Ponowna kompilacja
              go zdejmuje, więc po każdej zmianie pakiet trzeba podpisać jeszcze raz.
            </p>
            <div className="flex justify-end">
              <Button onClick={() => setSignedPackagePrompt(false)}>Rozumiem</Button>
            </div>
          </div>
        </Modal>
      )}
      {mergeWypisOpen && (
        <MergeWypisModal applicationId={app.id} onClose={() => setMergeWypisOpen(false)} />
      )}
      {editOpen && (
        <EditApplicationModal applicationId={app.id} onClose={() => setEditOpen(false)} />
      )}
      {kwPrompt && app.wpw_kw_warning && (
        <KwWarningConfirmModal
          items={[{
            row: { parcel: { id: app.wpw_parcel ?? app.id, teryt: app.wpw_parcel_teryt, short_number: null } },
            reason: app.wpw_kw_warning.reason,
          }]}
          actionLabel={kwPrompt.actionLabel}
          onCancel={() => setKwPrompt(null)}
          onConfirm={() => { const { proceed } = kwPrompt; setKwPrompt(null); proceed(); }}
        />
      )}
      {caseLinkOpen && (
        <Modal open onClose={() => setCaseLinkOpen(false)} title="Sprawa w Korespondencji dla pakietu">
          <div className="space-y-3">
            <p className="text-xs text-[var(--color-text-secondary)]">
              Wybierz sprawę tego projektu. Jeśli ma pozycję „Wniosek” bez pakietu, możesz podpiąć
              pakiet do niej — wysyłka dołoży wtedy dokumenty do tej pozycji zamiast zakładać drugą.
              Zamkniętą sprawę podpięcie otworzy ponownie (tylko status, po potwierdzeniu).
            </p>
            <CaseLinkPicker
              connectionId={app.connection}
              recipientOffice={app.recipient_office}
              allowClosed
              packageNumber={app.number}
              value={caseDraft}
              onChange={setCaseDraft}
            />
            {m.linkProceeding.error && (
              <p className="text-xs text-red-600">{apiError(m.linkProceeding.error)}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setCaseLinkOpen(false)}>Anuluj</Button>
              <Button
                disabled={!caseDraft || (!!caseDraft.closed && !caseDraft.reopen)}
                loading={m.linkProceeding.isPending}
                onClick={() => caseDraft && m.linkProceeding.mutate(
                  {
                    id: app.id, proceeding: caseDraft.proceeding, proceeding_item: caseDraft.item,
                    reopen_case: !!caseDraft.closed && !!caseDraft.reopen,
                  },
                  { onSuccess: () => setCaseLinkOpen(false) },
                )}
              >
                Podepnij
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {/* Zwrot do poprawy z kolejki „do akceptacji": potwierdzenie + opcjonalny powód,
          który zobaczy przekazujący (czerwony baner na wniosku + dzwonek). */}
      {rejectOpen && (
        <Modal open={rejectOpen} onClose={() => setRejectOpen(false)}
               title={`Zwróć do poprawy ${app.number}`} size="sm">
          <div className="space-y-3 text-sm">
            <p>
              Wniosek wróci do szkicu (edycja zostanie odblokowana)
              {app.approval_requested_by_name
                ? <>, a <b>{app.approval_requested_by_name}</b> dostanie powiadomienie</>
                : ", a przekazujący dostanie powiadomienie"}
              {" "}— razem z powodem, jeśli go wpiszesz.
            </p>
            <label className="block">
              <span className="section-label">Powód (opcjonalny)</span>
              <textarea className="input-base mt-1 w-full" rows={3} value={reviewNote}
                        onChange={(e) => setReviewNote(e.target.value)}
                        placeholder="Co poprawić przed ponownym przekazaniem?" />
            </label>
            {m.rejectApproval.isError && (
              <div className="text-xs text-red-600">{apiError(m.rejectApproval.error)}</div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setRejectOpen(false)}>Anuluj</Button>
              <Button loading={m.rejectApproval.isPending}
                      onClick={() => {
                        m.rejectApproval.mutate(
                          { id: app.id, note: reviewNote },
                          { onSuccess: () => { setReviewNote(""); setRejectOpen(false); } },
                        );
                      }}>
                <Undo2 size={13} className="mr-1" /> Zwróć do poprawy
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {/* „Zakończ" przy OTWARTEJ sprawie: rozstrzygnięcie wybiera człowiek (patrz modal). */}
      {completeOpen && (
        <CompleteApplicationModal
          app={app}
          open={completeOpen}
          onClose={() => setCompleteOpen(false)}
          pending={m.complete.isPending}
          error={m.complete.error ? apiError(m.complete.error) : null}
          onConfirm={(v) => m.complete.mutate({ id: app.id, ...v }, { onSuccess: () => setCompleteOpen(false) })}
        />
      )}
      {formatkiModal && (
        <GenerateFormatkiModal
          connectionId={app.connection}
          communes={packageCommunes}
          starting={m.generateFormatki.isPending}
          onClose={() => setFormatkiModal(false)}
          onStart={(opts) => {
            setFormatkiErr("");
            setFormatkiDone(false);
            m.generateFormatki.mutate(
              { id: app.id, opts },
              {
                onSuccess: (r) => {
                  setFormatkiTask({ taskId: r.task_id, sheetSetId: r.sheet_set_id });
                  setFormatkiModal(false);
                },
                onError: (e) => { setFormatkiErr(apiError(e)); setFormatkiModal(false); },
              },
            );
          }}
        />
      )}
    </div>
  );
}

/** Karta opłaty skarbowej OD SAMEGO WNIOSKU (ULICP: 598 zł za decyzję ULICP).
 *  Osobna od łańcucha pełnomocnictw, bo to inna opłata: inna kwota, inny urząd
 *  (adresat wniosku — organ wydający decyzję) i nie wisi na żadnym pełnomocnictwie. */
function ApplicationFeeCard({
  app, info, editable, onOpen,
}: {
  app: OfficialApplicationDetail;
  info: ApplicationFeeInfo;
  editable: boolean;
  onOpen: (mode?: "not_required") => void;
}) {
  const fee = info.fee;
  const paid = ["payment_uploaded", "acknowledged"].includes(fee?.status ?? "");
  const dead = ["rejected", "withdrawn"].includes(fee?.status ?? "");
  return (
    <div className="border border-[var(--color-border)] p-2 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex-1">
          <b>
            Opłata skarbowa od wniosku ({info.amount_default} zł
            {info.per_copy
              ? ` = ${info.copies} egz. × ${(Number(info.amount_default) / Math.max(info.copies, 1)).toFixed(2)} zł`
              : ""})
          </b>
          <span className="block text-[var(--color-text-muted)]">
            {info.label || "Opłata skarbowa od wniosku"} — do urzędu:{" "}
            <b className={info.office_name ? "" : "text-amber-700 dark:text-amber-400"}>
              {info.office_name || "— nie znamy, wskaż adresata wniosku albo urząd opłaty"}
            </b>{" "}
            ({info.source_label})
          </span>
        </span>
        {app.poa_fees_settled_externally ? (
          <Badge color="gray">opłata poza systemem</Badge>
        ) : app.application_fee_not_required ? (
          <>
            <Badge color="gray">
              opłata niepotrzebna: {app.application_fee_not_required_reason}
            </Badge>
            {editable && (
              <Button
                size="sm" variant="ghost"
                title="Zmień decyzję — popraw uzasadnienie albo jednak zleć opłatę"
                onClick={() => onOpen("not_required")}
              >
                Zmień
              </Button>
            )}
          </>
        ) : fee ? (
          <>
            <Badge color={paid ? "green" : dead ? "red" : "yellow"}>
              {fee.amount} zł: {FEE_LABELS[fee.status] ?? fee.status}
              {fee.paid_at ? ` ${fmtDate(fee.paid_at)}` : ""}
            </Badge>
            {UNPAID_FEE_STATUSES.includes(fee.status) && (
              <span className="text-[var(--color-text-muted)]">
                bez przelewu: kompilacja i wysyłka tylko świadomie (bez opłaty)
              </span>
            )}
            {editable && dead && (
              <Button
                size="sm" variant="secondary"
                title="Poproś o nową opłatę, wskaż inną albo oznacz jako niepotrzebną"
                onClick={() => onOpen()}
              >
                <Wallet size={12} className="mr-1" /> Rozlicz ponownie
              </Button>
            )}
          </>
        ) : editable ? (
          <>
            <Button size="sm" variant="secondary" onClick={() => onOpen()}>
              <Wallet size={12} className="mr-1" /> Opłata {info.amount_default} zł
            </Button>
            <Button
              size="sm" variant="ghost"
              title="Opłata od wniosku nie jest w tym przypadku należna (podaj uzasadnienie)"
              onClick={() => onOpen("not_required")}
            >
              Niepotrzebna
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** Okno opłaty OD WNIOSKU: nowa / wskazanie istniejącej / niepotrzebna — te same
 *  tryby co przy pełnomocnictwie. Kwota jest edytowalna (stawka zależy od zakresu
 *  wniosku i zmienia się ustawą), a urząd da się poprawić ręcznie. */
function ApplicationFeeModal({
  app, info, initialMode, candidates, onClose, onSubmit, onSetOffice, pending,
}: {
  app: OfficialApplicationDetail;
  info: ApplicationFeeInfo;
  initialMode?: "not_required";
  candidates: PoaFeeCandidate[];
  onClose: () => void;
  onSubmit: (payload: { mode: "new" | "existing" | "not_required"; fee?: string;
                        reason?: string; amount?: string; office_account_number?: string;
                        stamp_duty_office?: string }) => void;
  onSetOffice: (officeId: string) => void;
  pending: boolean;
}) {
  const [mode, setMode] = useState<"new" | "existing" | "not_required">(initialMode ?? "new");
  const [fee, setFee] = useState("");
  const [reason, setReason] = useState(app.application_fee_not_required_reason ?? "");
  const [amount, setAmount] = useState(info.amount_default || "");
  const [account, setAccount] = useState(info.bank_account || "");
  // "" = urząd z adresata wniosku; ustawione = wskazany ręcznie w tym oknie.
  const [office, setOffice] = useState(info.manual ? info.office_id ?? "" : "");
  const [officeTouched, setOfficeTouched] = useState(false);
  const { data: offices } = useSelectableOffices(office);
  const pickedOffice = (offices ?? []).find((o) => o.id === office);

  return (
    <Modal open onClose={onClose} title={`Opłata skarbowa od wniosku — ${app.number}`}>
      <div className="space-y-3 text-xs">
        <div className="rounded border border-[var(--color-border)] px-2.5 py-2 space-y-1.5">
          <div className="text-[var(--color-text-secondary)]">
            Opłatę od wniosku pobiera organ, który wydaje decyzję — czyli{" "}
            <b>adresat wniosku</b> (inaczej niż 17 zł za pełnomocnictwo, które idzie do
            urzędu siedziby mocodawcy):{" "}
            <b className={pickedOffice || info.office_name ? "" : "text-amber-700 dark:text-amber-400"}>
              {pickedOffice?.name || info.office_name || "— nie rozpoznano, wskaż urząd"}
            </b>
          </div>
          <SearchableSelect
            options={(offices ?? []).map((o) => ({
              value: o.id, label: o.city ? `${o.name} — ${o.city}` : o.name,
            }))}
            value={office}
            placeholder="Urząd opłaty (puste = adresat wniosku)…"
            disabled={pending}
            onChange={(e) => { setOffice(e.target.value); setOfficeTouched(true); }}
          />
          <button
            type="button" className="text-[var(--color-primary)] hover:underline"
            onClick={() => onSetOffice(office)} disabled={pending}
          >
            Zapisz sam urząd
          </button>
        </div>
        <div className="space-y-1">
          {([
            ["new", "Poproś o nową opłatę (obieg /official-fees; wniosek zaczeka jako szkic)"],
            ["existing", "Wskaż istniejącą opłatę (już zleconą albo opłaconą)"],
            ["not_required", "Opłata niepotrzebna (z uzasadnieniem)"],
          ] as const).map(([value, label]) => (
            <label key={value} className="flex items-center gap-2">
              <input type="radio" checked={mode === value} onChange={() => setMode(value)} />
              {label}
            </label>
          ))}
        </div>
        {mode === "new" && (
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="section-label">Kwota [PLN]</span>
              <input className="input-base mt-1" type="number" min="0" step="0.01"
                     value={amount} onChange={(e) => setAmount(e.target.value)} />
              <span className="block mt-0.5 text-[var(--color-text-muted)]">
                Domyślnie {info.amount_default} zł — zmień, gdy stawka albo zakres wniosku
                są inne.
              </span>
            </label>
            <label className="block">
              <span className="section-label">
                Numer konta urzędu {info.bank_account ? "" : "*"}
              </span>
              <input className="input-base mt-1 font-mono" placeholder="26 cyfr (PL…)"
                     value={account} onChange={(e) => setAccount(e.target.value)} />
            </label>
          </div>
        )}
        {mode === "existing" && (
          <label className="block">
            <span className="section-label">Opłata</span>
            <select className="input-base mt-1" value={fee} onChange={(e) => setFee(e.target.value)}>
              <PoaFeeOptionGroups options={candidates} />
            </select>
            <UnpaidPoaFeeNote
              fee={candidates.find((c) => c.id === fee)}
              consequence="Możesz ją wskazać — wniosek zaczeka jako szkic i nie skompilujesz go ani nie wyślesz, dopóki w opłacie (/official-fees) nie pojawi się potwierdzenie przelewu. Wtedy wróci tu sam, a potwierdzenie pojedzie z wysyłką — jako osobny plik obok pakietu."
            />
          </label>
        )}
        {mode === "not_required" && (
          <label className="block">
            <span className="section-label">Uzasadnienie *</span>
            <input className="input-base mt-1" value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Anuluj</Button>
          <Button
            size="sm" loading={pending}
            disabled={(mode === "existing" && !fee) || (mode === "not_required" && !reason)}
            onClick={() => onSubmit({
              mode, fee: fee || undefined, reason: reason || undefined,
              amount: amount || undefined,
              office_account_number: account || undefined,
              ...(officeTouched ? { stamp_duty_office: office } : {}),
            })}
          >
            Zapisz
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PoaFeeModal({
  app, linkId, initialMode, candidates, onClose, onSubmit, onSetOffice, pending,
}: {
  app: OfficialApplicationDetail;
  linkId: string;
  /** Otwarcie wprost w trybie „niepotrzebna" (przycisk przy ogniwie) — sugestia
   *  nie może wtedy przestawić trybu za użytkownika. */
  initialMode?: "not_required";
  candidates: PoaFeeCandidate[];
  onClose: () => void;
  onSubmit: (payload: { mode: "new" | "existing" | "not_required"; fee?: string; reason?: string;
                        office_account_number?: string; amount?: string;
                        transfer_name?: string; description?: string;
                        stamp_duty_office?: string }) => void;
  /** Ręczna korekta urzędu opłaty (dotyczy całego wniosku, nie jednego ogniwa). */
  onSetOffice: (officeId: string) => void;
  pending: boolean;
}) {
  const link = app.poas.find((l) => l.id === linkId);
  const [mode, setMode] = useState<"new" | "existing" | "not_required">(initialMode ?? "new");
  const [fee, setFee] = useState("");
  const [reason, setReason] = useState(link?.fee_not_required_reason ?? "");
  const [account, setAccount] = useState(app.stamp_duty_office.bank_account || "");
  // "" = rozpoznanie automatyczne; ustawione = urząd wskazany ręcznie w tym oknie.
  const [office, setOffice] = useState(
    app.stamp_duty_office.manual ? app.stamp_duty_office.office_id ?? "" : "",
  );
  const [officeTouched, setOfficeTouched] = useState(false);
  const [amount, setAmount] = useState("17.00");
  const [transferName, setTransferName] = useState("");
  const [description, setDescription] = useState("");
  const [docWarning, setDocWarning] = useState("");
  const [docFiles, setDocFiles] = useState<File[]>([]);

  // Smart prefill z PDF-a naliczenia (wezwanie/DOO) — ten sam ekstraktor co w wypisach.
  const docExtract = useMutation({
    mutationFn: (file: File) => extractApplicationFeeDoc(app.id, file),
    onSuccess: (d) => {
      setDocWarning("");
      setMode("new");
      if (d.amount) setAmount(d.amount);
      if (d.account_number) setAccount(d.account_number);
      // Tytuł płatności podany w dokumencie ma pierwszeństwo i idzie DOKŁADNIE tak,
      // jak go napisał urząd. Bez niego składamy własny: numer naliczenia (DOO/znak
      // sprawy) + „pełnomocnictwo". Bez numeru wniosku — jedna opłata 17 zł obsługuje
      // pełnomocnictwo w danym urzędzie i może być podpięta pod inny wniosek.
      const docNum = (d.doo_number || "").replace(/^\s*DOO[\s.:#-]*/i, "").trim();
      setTransferName(
        d.payment_title ||
          [
            docNum ? `DOO - nr ${docNum}` : "",
            d.case_sign || "",
            "pełnomocnictwo",
          ].filter(Boolean).join(" · "),
      );
      const officeInfo = [d.office_name, d.office_address].filter(Boolean).join(", ");
      const parts = [
        docNum ? `DOO ${docNum}` : "",
        d.case_sign ? `znak sprawy: ${d.case_sign}` : "",
        officeInfo ? `odbiorca: ${officeInfo}` : "",
      ].filter(Boolean);
      if (parts.length) setDescription(parts.join(" · "));
      if (!d.payment_title && !docNum && !d.case_sign) {
        setDocWarning("Nie znaleziono numeru naliczenia w dokumencie — sprawdź tytuł przelewu.");
      }
    },
    onError: (e: unknown) => {
      setDocWarning(apiError(e) || "Nie udało się odczytać dokumentu — wypełnij pola ręcznie.");
    },
  });

  // Sugestia AI: nowa vs istniejąca (miasto mocodawcy + ≤7 dni + niepodpięta do innego
  // wniosku do tego urzędu). Preselektuje tryb i opłatę raz, po załadowaniu.
  const { data: suggestion, isPending: suggestionLoading } = usePoaFeeSuggestion(app.id, true);
  const applySuggestion = () => {
    if (suggestion?.mode === "existing" && suggestion.fee) {
      setMode("existing");
      setFee(suggestion.fee.id);
    } else {
      setMode("new");
    }
  };
  const [applied, setApplied] = useState(false);
  useEffect(() => {
    if (!suggestion || applied) return;
    setApplied(true);
    if (!initialMode) applySuggestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion, applied]);
  const suggestedFeeId = suggestion?.mode === "existing" ? suggestion.fee?.id : undefined;

  // Merge the global candidate list with the suggestion's matches so the suggested fee is
  // always a selectable option (it may fall outside the global „last 50").
  const feeOptions = (() => {
    const byId = new Map(candidates.map((c) => [c.id, c]));
    for (const c of suggestion?.candidates ?? []) if (!byId.has(c.id)) byId.set(c.id, c);
    return [...byId.values()];
  })();

  return (
    <Modal open onClose={onClose} title={`Opłata skarbowa 17 zł — ${link?.poa.attorney_display ?? ""}`}>
      <div className="space-y-3 text-xs">
        <StampDutyOfficePicker
          info={app.stamp_duty_office} value={office} disabled={pending}
          onChange={(value) => { setOffice(value); setOfficeTouched(true); }}
          onSaveOnly={() => onSetOffice(office)}
        />
        <PoaSuggestionHint
          loading={suggestionLoading}
          suggestion={suggestion}
          showApply={!!suggestion && (mode !== suggestion.mode
            || (suggestion.mode === "existing" && fee !== suggestedFeeId))}
          onApply={applySuggestion}
        />
        <div className="space-y-1">
          {([
            ["new", "Poproś o nową opłatę (obieg /official-fees; wniosek zaczeka jako szkic)"],
            ["existing", "Podepnij istniejącą opłatę „za pełnomocnictwo”"],
            ["not_required", "Opłata niepotrzebna (z uzasadnieniem)"],
          ] as const).map(([value, label]) => (
            <label key={value} className="flex items-center gap-2">
              <input type="radio" checked={mode === value} onChange={() => setMode(value)} />
              {label}
            </label>
          ))}
        </div>
        {mode === "new" && (
          <>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="shrink-0" />
                <span>
                  <b>PDF z naliczeniem (wezwanie/DOO)</b> — kwota, konto i tytuł uzupełnią się
                  automatycznie (AI)
                </span>
              </div>
              <FileDropZone
                files={docFiles}
                accept=".pdf"
                onFilesChange={(fs) => {
                  setDocFiles(fs);
                  if (fs[0]) docExtract.mutate(fs[0]);
                }}
              />
              {docExtract.isPending && (
                <div className="flex items-center gap-2 text-[var(--color-text-muted)]">
                  <Spinner size="sm" /> Analizuję naliczenie…
                </div>
              )}
              {docWarning && <div className="text-amber-600">{docWarning}</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label">Kwota [PLN]</span>
                <input className="input-base mt-1" type="number" min="0" step="0.01"
                       value={amount} onChange={(e) => setAmount(e.target.value)} />
              </label>
              <label className="block">
                <span className="section-label">
                  Numer konta urzędu opłaty {app.stamp_duty_office.bank_account ? "" : "*"}
                </span>
                <input className="input-base mt-1 font-mono" placeholder="26 cyfr (PL…)"
                       value={account} onChange={(e) => setAccount(e.target.value)} />
              </label>
            </div>
            {transferName && (
              <label className="block">
                <span className="section-label">Tytuł przelewu</span>
                <input className="input-base mt-1" value={transferName}
                       onChange={(e) => setTransferName(e.target.value)} />
              </label>
            )}
          </>
        )}
        {mode === "existing" && (
          <label className="block">
            <span className="section-label">Opłata</span>
            <select className="input-base mt-1" value={fee} onChange={(e) => setFee(e.target.value)}>
              <PoaFeeOptionGroups options={feeOptions} suggestedFeeId={suggestedFeeId} />
            </select>
            <UnpaidPoaFeeNote
              fee={feeOptions.find((c) => c.id === fee)}
              consequence="Możesz ją podpiąć — wniosek zaczeka jako szkic i nie skompilujesz go ani nie wyślesz, dopóki w opłacie (/official-fees) nie pojawi się potwierdzenie przelewu. Wtedy wróci tu sam (dostaniesz powiadomienie), a potwierdzenie pojedzie z wysyłką automatycznie — jako osobny plik obok pakietu (nie scalamy go, żeby nie stracić podpisu elektronicznego)."
            />
          </label>
        )}
        {mode === "not_required" && (
          <label className="block">
            <span className="section-label">Uzasadnienie *</span>
            <input className="input-base mt-1" value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Anuluj</Button>
          <Button
            size="sm" loading={pending}
            disabled={(mode === "existing" && !fee) || (mode === "not_required" && !reason)}
            onClick={() => onSubmit({
              mode, fee: fee || undefined, reason: reason || undefined,
              office_account_number: account || undefined,
              amount: amount || undefined,
              transfer_name: transferName || undefined,
              description: description || undefined,
              ...(officeTouched ? { stamp_duty_office: office } : {}),
            })}
          >
            Zapisz
          </Button>
        </div>
      </div>
    </Modal>
  );
}
